// GameMap component - Main game canvas for player view

import { css, html } from 'lit';
import { GameEventsController } from '../../controllers/game-events-controller.js';
import { InteractionStateController } from '../../controllers/interaction-state-controller.js';
import { MapStateController } from '../../controllers/map-state-controller.js';
import { TouchController } from '../../controllers/touch-controller.js';
import { ViewStateController } from '../../controllers/view-state-controller.js';
import { chebyshevDisk } from '../../lib/aoe-geometry.js';
import { BaseComponent } from '../../lib/base-component.js';
import { EphemeralClient } from '../../lib/ephemeral.js';
import { buildMovementTrail } from '../../lib/movement-utils.js';
import { cellToPixelCenter } from '../../rendering/coordinate-transforms.js';
import { preloadImages } from '../../rendering/image-cache.js';
import { renderGameMap } from '../../rendering/render-pipeline.js';
import { clearTileCache, initTileCache, loadTilesForCells } from '../../rendering/tile-cache.js';
import { debugState } from '../../state/debug-state.js';
import { eventAnimationState } from '../../state/event-animation-state.js';
import { interactionState } from '../../state/interaction-state.js';
import { mapState } from '../../state/map-state.js';
import { platformState } from '../../state/platform-state.js';
import { viewState } from '../../state/view-state.js';
import './initiative-reveal.js';
import { api } from '../../lib/api.js';
import { authState } from '../../lib/auth.js';
import { musicManager } from '../../lib/music-manager.js';

// Import UI components
import '../shared/toolbar-button.js';
import '../shared/toolbar-well.js';
import '../shared/drawer.js';
import '../shared/drawer-bar.js';
import '../shared/view-controls.js';
import './turn-banner.js';
import './game-restart-banner.js';
import './mobile-turn-bar.js';
import './placement-banner.js';
import './waiting-banner.js';
import './tavern-waiting-banner.js';
import './prep-phase-banner.js';
import './rest-action-modal.js';
import './spell-choices-modal.js';
import { renderActionCard } from './action-card.js';
import { actionCardStyles } from './action-card-styles.js';
import './victory-overlay.js';
import './game-over-overlay.js';
import './spectator-end-overlay.js';
import './game-menu.js';
import './door-panel.js';
import './callout.js';
import './center-announcement.js';
import './range-indicator.js';
import './rotate-cursor.js';
import './character-sheet-drawer.js';
import './equipment-screen.js';
import { renderBackpackIcon } from '../shared/icons.js';
import './users-list.js';
import './info-drawer.js';
import './effects-drawer.js';
import './music-controls.js';
import '../shared/help-message.js';
import '../shared/confirm-delete.js';
import {
  isHelpMessageDismissed,
  isHelpMessageSuppressedInGame,
  setHelpMessageDismissed,
  suppressHelpMessageInGame,
} from '../../lib/help-state.js';

/**
 * @typedef {import('../../types/jsdoc-types.js').Token} Token
 * @typedef {import('../../types/jsdoc-types.js').Character} Character
 * @typedef {import('../../types/jsdoc-types.js').TokenDirection} TokenDirection
 */

/**
 * @typedef {{col: number, row: number, illumination: string, visibility?: string, personallyVisible?: boolean, perceivedViaDarkvision?: boolean}} HelpCell
 * @typedef {object} HelpMessageDef
 * @property {string} id - Stable id used for dismiss persistence and sessionStorage suppression.
 * @property {string} heading - Modal heading.
 * @property {(ctx: {cells: HelpCell[]}) => boolean} shouldShow - Predicate evaluated on each mapState change.
 * @property {(html: typeof import('lit').html) => unknown} body - Body content factory; receives `html` to avoid pulling lit at module top-level.
 */

/**
 * Registry of in-game help messages. To add a new one, append an entry —
 * no other plumbing required. At most one is shown at a time; on dismiss
 * the next mapState change re-evaluates the registry in order.
 *
 * @type {HelpMessageDef[]}
 */
const HELP_MESSAGES = [
  {
    id: 'personal-visibility',
    heading: 'Map squares your PC cannot see are greyed out',
    shouldShow: ({ cells }) => cells.some((c) => c.personallyVisible === false),
    body: (h) => h`
      <p>
        The map shows everything the party can see, together. Map
        squares that your own PC can see are in color and those your
        PC can't see are shown in greyscale.
      </p>
      <p>
        You can only attack or target map squares within your own
        line of sight — look for map squares shown in full color.
      </p>
    `,
  },
  {
    id: 'darkvision',
    heading: 'Darkvision',
    shouldShow: ({ cells }) => cells.some((c) => c.perceivedViaDarkvision),
    body: (h) => h`
      <p>
        Your PC has Darkvision. Within range, Darkness is seen as
        Dim Light, and Dim Light is seen as Bright Light.
      </p>
      <p>
        Map squares (and what is in them) you see by Darkvision are
        shown in shades of grey.
      </p>
    `,
  },
];

// localStorage key for the "don't ask again" preference on the empty-
// square attack confirm dialog. Per-user; defaults to off (player gets
// asked the first time and every time after, unless they tick the box).
const EMPTY_SQUARE_CONFIRM_DISMISSED_KEY = 'd3-gamemap-emptySquareConfirmDismissed';

/**
 * GameMap component
 * Main canvas component for player game view
 */
class GameMap extends BaseComponent {
  static properties = {
    gameId: { type: String, attribute: 'game-id' },
    currentUserId: { type: String, attribute: 'current-user-id' },
    backendUrl: { type: String, attribute: 'backend-url' },
    spectator: { type: Boolean, attribute: 'spectator', reflect: true },
    width: { type: String },
    height: { type: String },
    // Internal state
    _canvasWidth: { type: Number, state: true },
    _canvasHeight: { type: Number, state: true },
    _mode: { type: String, state: true },
    _drawerOpen: { type: Boolean, state: true },
    _drawerActiveView: { type: String, state: true },
    _infoDrawerMessages: { type: Array, state: true },
    _infoBadge: { type: Boolean, state: true },
    _gameMenuOpen: { type: Boolean, state: true },
    _showRestartConfirm: { type: Boolean, state: true },
    _restartReplaySeed: { type: Boolean, state: true },
    _showGrid: { type: Boolean, state: true },
    _showWalls: { type: Boolean, state: true },
    _imagesLoading: { type: Boolean, state: true },
    _callouts: { type: Array, state: true },
    _centerAnnouncement: { type: Object, state: true },
    _adventureCompleted: { type: Boolean, state: true },
    _partyWiped: { type: Boolean, state: true },
    _turnState: { type: Object, state: true },
    _isPlacementMode: { type: Boolean, state: true },
    _placementCharacterId: { type: String, state: true },
    _isWaitingForOtherPlayers: { type: Boolean, state: true },
    _spectatorPaused: { type: Boolean, state: true },
    _activeDoor: { type: Object, state: true },
    _initiativeRevealState: { type: Object, state: true },
    _mobileSheetView: { type: String, state: true },
    /** ID of the active help-message (or null when none is showing). One at a time. */
    _activeHelpId: { type: String, state: true },
    _debug: { type: Boolean, state: true },
    _seedCopied: { type: Boolean, state: true },
    _showActionsModal: { type: Boolean, state: true },
    /** When non-null, shows the Ready trigger-chooser modal (#266). */
    _readyChooserAction: { type: Object, state: true },
    /** When non-null, shows the spell-choices modal (Resistance) before targeting begins. */
    _spellChoicesContext: { type: Object, state: true },
    _openRestAction: { type: Object, state: true },
    /** Pending empty-square attack awaiting player confirmation; null when dismissed or never opened. */
    _emptySquareConfirm: { type: Object, state: true },
    /** True while the equipment-screen modal is open. */
    _equipmentScreenOpen: { type: Boolean, state: true },
    // _pendingEquipmentSwap holds the staged free Attack-bundled weapon
    // swap. The next pc:act:attack the player submits will carry these
    // as `prefixEquipmentChanges` so the server commits swap+attack
    // atomically. Field type is documented on the constructor initializer.
    _pendingEquipmentSwap: { type: Object, state: true },
    // Player's "Re-equip after throw" preference for this game.
    // Persisted to localStorage so the choice survives page reloads.
    // Default ON (the common case for a stack-of-daggers fighter); the
    // player toggles it off if they want their hand empty after the throw.
    _reEquipThrownPref: { type: Boolean, state: true },
  };

  static styles = [
    actionCardStyles,
    css`
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }

    .game-map-wrapper {
      position: relative;
      width: 100%;
      height: 100%;
      display: flex;
      overflow: hidden;
    }

    .game-map-wrapper.mobile {
      flex-direction: column;
    }

    .game-map-container {
      position: relative;
      flex: 1;
      min-width: 0;
      overflow: hidden;
      background: #1a1a1a;
    }

    .game-map {
      position: absolute;
      top: 0;
      left: 0;
      cursor: default;
    }

    .game-map.can-move {
      cursor: grab;
    }

    .game-map.is-moving {
      cursor: grabbing;
    }

    .game-map.no-movement {
      cursor: not-allowed;
    }

    .game-map.mode-pan {
      cursor: grab;
    }

    .game-map.mode-pan.is-panning {
      cursor: grabbing;
    }

    .loading-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 100;
    }

    .loading-spinner {
      width: 48px;
      height: 48px;
      border: 4px solid #333;
      border-top-color: #4a9eff;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    .loading-text {
      margin-top: 16px;
      color: #ccc;
      font-size: 14px;
    }

    .tools-sidebar {
      position: absolute;
      top: 20px;
      left: 20px;
      display: flex;
      gap: 8px;
      z-index: 10;
    }

    /* .tool-separator styling lives on toolbar-well via ::slotted so the
       rule is direction-aware (column vs row). */

    .grid-debug {
      position: absolute;
      bottom: 100px;
      right: 20px;
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 8px 12px;
      border-radius: 6px;
      border: 1px solid #444;
      backdrop-filter: blur(4px);
      font-size: 14px;
      font-family: monospace;
      z-index: 100;
      pointer-events: none;
    }

    .grid-debug .seed-row {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .grid-debug .seed-copy {
      pointer-events: auto;
      background: transparent;
      border: 1px solid #555;
      color: inherit;
      border-radius: 4px;
      padding: 0 6px;
      cursor: pointer;
      font-family: inherit;
      font-size: 12px;
      line-height: 1.4;
    }

    .grid-debug .seed-copy:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    .top-banners-container {
      position: absolute;
      top: 20px;
      left: 0;
      right: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      z-index: 20;
      pointer-events: none;
    }

    .top-banners-container > * {
      pointer-events: auto;
    }

    .targeting-banner {
      position: absolute;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      align-items: center;
      gap: 16px;
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 12px 24px;
      border-radius: 8px;
      border: 1px solid #444;
      backdrop-filter: blur(4px);
      font-size: 16px;
      white-space: nowrap;
      z-index: 200;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    }

    .targeting-icon {
      display: flex;
      align-items: center;
    }

    .targeting-text {
      font-weight: 600;
    }

    .cancel-targeting-btn {
      background: #ef4444;
      border: none;
      color: white;
      padding: 6px 16px;
      border-radius: 6px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.2s;
    }

    .cancel-targeting-btn:hover {
      background: #dc2626;
    }

    .confirm-targeting-btn {
      background: #22c55e;
      border: none;
      color: white;
      padding: 8px 16px;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.2s;
    }

    .confirm-targeting-btn:hover {
      background: #16a34a;
    }

    .no-tokens-banner {
      display: flex;
      align-items: center;
      gap: 8px;
      background: rgba(0, 0, 0, 0.8);
      color: #ef4444;
      padding: 12px 24px;
      border-radius: 8px;
      border: 1px solid #444;
      backdrop-filter: blur(4px);
      font-size: 15px;
      font-weight: 500;
    }

    .extra-attack-modal-backdrop {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10001;
    }

    .extra-attack-prompt {
      display: flex;
      flex-direction: column;
      gap: 16px;
      max-width: 560px;
      background: rgba(0, 0, 0, 0.95);
      color: white;
      padding: 20px 24px;
      border-radius: 8px;
      border: 1px solid rgba(255, 255, 255, 0.3);
      font-size: 16px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.8);
      pointer-events: auto;
    }

    .extra-attack-prompt-text {
      font-weight: 600;
    }

    .extra-attack-prompt-description {
      margin-top: 4px;
      font-weight: 400;
      font-size: 13px;
      color: #d4d4d8;
    }

    .extra-attack-prompt-actions {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
    }

    .extra-attack-accept-btn {
      background: #22c55e;
      border: none;
      color: white;
      padding: 8px 16px;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.2s;
    }

    .extra-attack-accept-btn:hover:not(:disabled) {
      background: #16a34a;
    }

    .extra-attack-accept-btn:disabled {
      background: #555;
      color: #aaa;
      cursor: not-allowed;
    }

    .extra-attack-skip-btn {
      background: #ef4444;
      border: none;
      color: white;
      padding: 6px 16px;
      border-radius: 6px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.2s;
    }

    .extra-attack-skip-btn:hover {
      background: #dc2626;
    }

    .drawer-view {
      height: 100%;
      overflow-y: auto;
    }

    /* Mobile */
    .mobile-toolbar {
      position: absolute;
      top: 12px;
      left: 12px;
      display: flex;
      gap: 8px;
      z-index: 10;
    }

    .mobile-tool-btn {
      width: 40px;
      height: 40px;
      border-radius: 8px;
      border: 1px solid rgba(255, 255, 255, 0.2);
      background: rgba(0, 0, 0, 0.7);
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      backdrop-filter: blur(4px);
      -webkit-tap-highlight-color: transparent;
      position: relative;
    }

    .mobile-tool-btn:active {
      background: rgba(0, 0, 0, 0.9);
    }

    .mobile-tool-btn .badge-dot {
      position: absolute;
      top: 4px;
      right: 4px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #ef4444;
    }

    .mobile-sheet-backdrop {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.3);
      z-index: 29;
    }

    .mobile-bottom-sheet {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 95vh;
      background: #f2f2f7;
      border-radius: 12px 12px 0 0;
      z-index: 30;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      box-shadow: 0 -2px 20px rgba(0, 0, 0, 0.3);
    }

    .mobile-sheet-handle {
      display: flex;
      justify-content: center;
      padding: 8px 0 4px;
      flex-shrink: 0;
    }

    .mobile-sheet-handle-pill {
      width: 36px;
      height: 5px;
      border-radius: 3px;
      background: #c0c0c0;
    }

    .mobile-sheet-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 4px 16px 10px;
      flex-shrink: 0;
    }

    .mobile-sheet-title {
      color: #000000;
      font-size: 1rem;
      font-weight: 600;
    }

    .mobile-sheet-close {
      background: none;
      border: none;
      color: #007aff;
      font-size: 0.95rem;
      font-weight: 500;
      cursor: pointer;
      padding: 0.25rem 0.5rem;
      -webkit-tap-highlight-color: transparent;
    }

    .mobile-sheet-content {
      flex: 1;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      background: #ffffff;
      border-top: 1px solid #e0e0e0;
      padding: 1rem;
    }

    .mobile-bottom-sheet.dark {
      background: #222;
    }

    .mobile-bottom-sheet.dark .mobile-sheet-handle-pill {
      background: #555;
    }

    .mobile-bottom-sheet.dark .mobile-sheet-title {
      color: #ffffff;
    }

    .mobile-bottom-sheet.dark .mobile-sheet-content {
      background: #1a1a1a;
      border-top-color: #333;
    }

    /* Action / bonus-action modal styles. The backdrop uses position:absolute
       with inset:0 so it sizes to .game-map-container (its position:relative
       ancestor) — which already excludes the side drawer-panel because the
       drawer is a flex sibling, not an ancestor. */
    .modal-backdrop {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.3);
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding-top: 100px;
      z-index: 10000;
    }

    .modal-menu {
      background: rgba(0, 0, 0, 0.95);
      border: 1px solid rgba(255, 255, 255, 0.3);
      border-radius: 8px;
      /* width: 100% lets the modal claim the available horizontal space (up
         to max-width) so the grid inside can auto-fill multiple columns
         when the viewport is wide. Without this, the modal sized to its
         content width — which in a single-column grid is just one column. */
      width: 100%;
      min-width: min(320px, 90vw);
      max-width: min(1100px, 95vw);
      max-height: calc(100vh - 140px);
      display: flex;
      flex-direction: column;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.8);
      pointer-events: auto;
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 20px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.2);
      flex-shrink: 0;
    }

    .modal-header h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: white;
    }

    .modal-close {
      background: none;
      border: none;
      color: white;
      font-size: 28px;
      cursor: pointer;
      padding: 0;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      transition: background 0.2s;
    }

    .modal-close:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    .modal-content {
      padding: 12px;
      overflow-y: auto;
      flex: 1 1 auto;
      min-height: 0;
    }

    /* .menu-item / .menu-item.rich + sub-classes live in
       ./action-card-styles.js so <rest-action> can reuse them. */

    .action-info {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .action-name {
      font-weight: 500;
    }

    .spell-slot-info {
      font-size: 12px;
      color: white;
      font-weight: 600;
      margin-left: 4px;
    }


    .action-stats {
      display: flex;
      align-items: center;
      gap: 12px;
      font-size: 12px;
    }

    .action-attack {
      opacity: 0.9;
      font-weight: 600;
      min-width: 30px;
      text-align: right;
    }

    .action-damage {
      opacity: 0.8;
    }

    .torch-action-icon {
      color: #f59e0b;
    }

    .dash-action-icon {
      color: #3b82f6;
    }

    .dodge-action-icon {
      color: #22c55e;
    }

    .modal-icon {
      width: 16px;
      height: 16px;
    }

    .modal-icon-xl {
      width: 28px;
      height: 28px;
    }

    .modal-icon-lg {
      width: 24px;
      height: 24px;
    }

    .actions-grid {
      display: grid;
      grid-auto-flow: column;
      gap: 1rem;
    }

    /* Wrap a single action card whose card has a per-action toggle (the
       "Re-equip after throw" checkbox today). The wrapper takes over the
       card's outer border/background so the button + toggle read as one
       integrated card; the button inside drops its border to merge
       seamlessly. */
    .action-cell-with-toggle {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100%;
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 6px;
      overflow: hidden;
      background: transparent;
      transition: all 0.2s;
    }

    .action-cell-with-toggle:hover {
      background: rgba(59, 130, 246, 0.15);
      border-color: rgba(59, 130, 246, 0.5);
    }

    .action-cell-with-toggle > .menu-item.rich {
      border: 0;
      border-radius: 0;
      flex: 1;
      background: transparent;
    }

    .action-cell-with-toggle > .menu-item.rich:hover:not(:disabled) {
      background: transparent;
      border-color: transparent;
    }

    /* Bootstrap form-check inside the action card. Pad to line up with the
       card's icon column; trim the default form-check margin so the toggle
       sits flush against the bottom of the card content. */
    .re-equip-toggle {
      padding: 4px 16px 12px 40px;
      margin: 0;
      font-size: 12px;
      color: rgba(255, 255, 255, 0.85);
      cursor: pointer;
      user-select: none;
    }

    .re-equip-toggle .form-check-input {
      cursor: pointer;
      width: 1.25em;
      height: 1.25em;
      margin-top: 0.1em;
    }

  `,
  ];

  constructor() {
    super();
    this.gameId = '';
    this.currentUserId = '';
    this.backendUrl = '';
    this.spectator = false;
    this.width = '100%';
    this.height = '100%';

    // Internal state
    this._canvasWidth = 0;
    this._canvasHeight = 0;
    this._mode = 'select';
    this._drawerOpen = false;
    /** @type {string|null} */
    this._drawerActiveView = null;
    /** @type {import('./info-drawer.js').InfoMessage[]} */
    this._infoDrawerMessages = [];
    this._infoBadge = false;
    this._infoMessageCounter = 0;
    this._gameMenuOpen = false;
    this._equipmentScreenOpen = false;
    /** @type {{ characterId: string, changes: any[], phase: 'pre'|'post', previewCharacter?: any | null } | null} */
    this._pendingEquipmentSwap = null;
    // Default true; hydrated from localStorage once we know our gameId.
    this._reEquipThrownPref = true;
    this._showRestartConfirm = false;
    this._restartReplaySeed = false;
    this._showGrid = true;
    this._showWalls = false;
    this._imagesLoading = true;
    /** @type {Array<{id: number, actorId: string, text: string, x: number, y: number, color: string, delayMs: number, holdMs: number, fadeMs: number}>} */
    this._callouts = [];
    /** @type {{message: string, type: string}|null} */
    this._centerAnnouncement = null;
    this._adventureCompleted = false;
    this._partyWiped = false;
    this._showRotateCursor = false;
    this._rotateCursorX = 0;
    this._rotateCursorY = 0;
    /** @type {import('../../state/map-state.js').TurnState|null} */
    this._turnState = null;
    this._isPlacementMode = false;
    /** @type {string|null} */
    this._placementCharacterId = null;
    this._isWaitingForOtherPlayers = false;
    this._spectatorPaused = false;
    /** @type {object|null} */
    this._activeDoor = null;
    /** @type {{id: string, name: string, slug?: string, category?: string, targeting?: {type?: string, range?: {normal?: number}, filter?: string}} | null} */
    this._pendingAction = null;
    /** @type {string|null} */
    this._mobileSheetView = null;
    /** @type {TouchController|null} */
    this._touchController = null;
    /** @type {string|null} */
    this._touchDragMode = null;
    this._lastTouchPanX = 0;
    this._lastTouchPanY = 0;

    // Reactive controllers
    this._mapState = new MapStateController(this, {
      onChange: () => {
        this._turnState = mapState.turnState;
        this._adventureCompleted = mapState.adventureCompleted || false;
        this._partyWiped = mapState.partyWiped || false;
        this._checkPlacementMode();
        this._emitSnapshot();
        this._handleVisibilityChange();
        this._maybeShowHelp();
        this._updateMusic();
      },
    });
    this._viewState = new ViewStateController(this, {
      onChange: () => this._saveViewportState(),
    });
    this._interaction = new InteractionStateController(this);
    this._gameEvents = new GameEventsController(this, {
      entityType: 'game',
      storagePrefix: 'game',
    });

    // Animation state
    this._animationFrameId = null;
    this._resizeObserver = null;
    this._resizeTimeout = null;
    this._calloutIdCounter = 0;

    // Mouse state
    this._isMouseDown = false;
    this._isRightMouseDown = false;
    this._isPanning = false;
    this._lastMouseX = 0;
    this._lastMouseY = 0;
    this._currentGridCol = 0;
    this._currentGridRow = 0;

    // Initial centering flag
    this._initialCenteringDone = false;
    // Spectator-only: separate flag for the adventure-map zoom-to-fit so
    // it fires once after the lobby's _centerOnWaitingRoomSeats has
    // already set _initialCenteringDone.
    this._spectatorMapCentered = false;

    // Canvas references
    /** @type {HTMLCanvasElement|null} */
    this._canvas = null;
    /** @type {CanvasRenderingContext2D|null} */
    this._ctx = null;
    /** @type {Element|null} */
    this._container = null;

    // Tile cache tracking
    /** @type {import('../../state/map-state.js').MapVisibility|null} */
    this._lastVisibilityRef = null;

    // Help-message state. _activeHelpId is the id of the help message
    // currently shown (or null), one at a time. _triggeredHelpIds latches
    // ids we've already evaluated this session so we don't repeatedly fire
    // the predicate after dismissal — the user's "Don't show again"
    // preference lives in localStorage via lib/help-state.js, and the
    // sessionStorage suppression lives there too. New help messages get
    // wired by adding an entry to HELP_MESSAGES at the top of this file.
    this._activeHelpId = null;
    /** @type {Set<string>} */
    this._triggeredHelpIds = new Set();

    // Action / bonus-action modal visibility state. The modal is owned here
    // (not by turn-banner) so its position:absolute backdrop sizes to
    // .game-map-container and naturally excludes the side drawer-panel.
    this._showActionsModal = false;
    /** @type {import('../../types/jsdoc-types.js').RestAction | null} */
    this._openRestAction = null;
    /** @type {{col:number,row:number}|null} */
    this._emptySquareConfirm = null;
    /** @type {(event: KeyboardEvent) => void} */
    this._boundHandleModalEscape = this._handleModalEscape.bind(this);
  }

  /**
   * Walk the HELP_MESSAGES registry and surface the first eligible message.
   * Eligibility is: predicate true for current map state, not already
   * triggered this session, not suppressed for the current game, not
   * dismissed permanently. At most one help is shown at a time; the next
   * mapState change re-runs this check after dismissal.
   *
   * Skipped during PC placement because the renderer suppresses
   * desaturation overlays there (#227) — the next mapState update after
   * placement ends will re-run this.
   */
  async _maybeShowHelp() {
    if (this._activeHelpId) return;
    if (interactionState.placement.isPlacing) return;
    const cells = mapState.mapVisibility?.gridCells;
    if (!cells || cells.length === 0) return;

    for (const msg of HELP_MESSAGES) {
      if (this._triggeredHelpIds.has(msg.id)) continue;
      if (!msg.shouldShow({ cells })) continue;
      this._triggeredHelpIds.add(msg.id);
      if (isHelpMessageSuppressedInGame(this.gameId, msg.id)) continue;
      const dismissed = await isHelpMessageDismissed(msg.id);
      if (dismissed) continue;
      this._activeHelpId = msg.id;
      return;
    }
  }

  _handleHelpDismiss(e) {
    const id = this._activeHelpId;
    if (!id) return;
    if (e.detail.dontShowAgain) {
      setHelpMessageDismissed(id, true);
    } else {
      suppressHelpMessageInGame(this.gameId, id);
    }
    this._activeHelpId = null;

    // Ephemeral (non-persisted) cross-player gestures: placement hover,
    // pointer signals, etc. Wired up in firstUpdated when gameId is set.
    /** @type {EphemeralClient|null} */
    this._ephemeral = null;
    /** @type {() => void | null} */
    this._unsubscribePlacementHover = null;
    /** @type {() => void | null} */
    this._unsubscribeEphemeralClear = null;
    /** Last (col,row) we published so we throttle by cell, not pixel. */
    this._lastPublishedHoverCell = null;
  }

  async connectedCallback() {
    // These must run before super.connectedCallback() because BaseComponent
    // awaits Bootstrap styles, which yields to Lit's first update microtask.
    // That fires firstUpdated → _connectSSE → loadEvents → loadStateSnapshot
    // → notify, which needs currentGameId set and saved viewport restored
    // (otherwise auto-centering overwrites the user's saved pan/zoom).
    initTileCache(this.gameId, { spectator: this.spectator });
    this._loadSavedState();
    this._debug = debugState.enabled;
    this._unsubscribeDebug = debugState.subscribe(() => {
      this._debug = debugState.enabled;
    });
    // Re-render on event-animation queue idle/active transitions so
    // the extra-attack prompt appears once callouts (and any future
    // event-driven animations) have settled.
    this._unsubscribeEventAnim = eventAnimationState.subscribe(() => {
      this.requestUpdate();
    });
    document.addEventListener('keydown', this._boundHandleModalEscape);
    await super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._boundHandleModalEscape);
    this._stopRenderLoop();
    // Controllers handle unsubscribe + SSE disconnect automatically
    this._cleanupResizeObserver();
    this._touchController?.destroy();
    this._touchController = null;
    clearTileCache();
    musicManager.switchTrack('silent');
    this._unsubscribePlacementHover?.();
    this._unsubscribeEphemeralClear?.();
    this._unsubscribeDebug?.();
    this._unsubscribeEventAnim?.();
    clearTimeout(this._seedCopiedTimer);
    this._ephemeral?.disconnect();
    this._ephemeral = null;
    // Clear the drawer-width signal so other views don't inherit our
    // game-only inset.
    document.documentElement.style.removeProperty('--game-drawer-width');
  }

  /** @param {Map<string, unknown>} changedProperties */
  updated(changedProperties) {
    super.updated?.(changedProperties);
    // Publish the drawer width to the document root as a CSS variable so
    // descendant components in any shadow DOM (e.g. the turn-banner action
    // modal) can shrink their viewport-fixed overlays away from the drawer
    // without prop-drilling. Drawer-panel default width is 320px (see
    // shared/drawer.js). The lobby (#381) forces the drawer open without
    // touching `_drawerOpen`, so re-publish on every update — cheap, idempotent.
    const drawerVisuallyOpen = mapState.waitingRoom?.isActive === true || this._drawerOpen;
    document.documentElement.style.setProperty('--game-drawer-width', drawerVisuallyOpen ? '320px' : '0px');
    if (changedProperties.has('_equipmentScreenOpen') && this.gameId) {
      // Persist whether the equipment manager is open so a refresh
      // restores it. Scoped per-game (see initial load).
      localStorage.setItem(`d3-gamemap-equipmentScreenOpen-${this.gameId}`, String(this._equipmentScreenOpen));
    }
    // (Re)init Bootstrap tooltips on the action-modal info icons. Lit
    // re-renders the cards as the action list changes, so dispose the
    // previous batch and rebuild from the current DOM each pass.
    this._refreshActionTooltips();
  }

  _refreshActionTooltips() {
    if (this._actionTooltipInstances) {
      this._actionTooltipInstances.forEach((t) => {
        t.dispose();
      });
      this._actionTooltipInstances = undefined;
    }
    if (!this._showActionsModal) return;
    const Tooltip = /** @type {{bootstrap?: {Tooltip: {new(el: Element, opts?: object): {dispose(): void}}}}} */ (
      window
    ).bootstrap?.Tooltip;
    if (!Tooltip) return;
    /** @type {{dispose: Function}[]} */
    this._actionTooltipInstances = [];
    const triggers = this.renderRoot?.querySelectorAll('[data-bs-toggle="tooltip"]') || [];
    // The actions modal sits at z-index 10000; Bootstrap's default
    // tooltip z-index (1080) would render the tooltip *behind* the
    // modal-backdrop. customClass bumps it via the global style below.
    if (!document.querySelector('#action-tooltip-style')) {
      const style = document.createElement('style');
      style.id = 'action-tooltip-style';
      style.textContent = `
        .action-tooltip { z-index: 10010 !important; }
        .action-tooltip .tooltip-inner {
          background: rgba(0, 0, 0, 0.92);
          border: 1px solid white;
          color: white;
          max-width: 320px;
          text-align: left;
        }
        .action-tooltip .tooltip-arrow::before {
          border-top-color: white !important;
          border-bottom-color: white !important;
          border-left-color: white !important;
          border-right-color: white !important;
        }
      `;
      document.head.appendChild(style);
    }
    triggers.forEach((el) => {
      this._actionTooltipInstances.push(new Tooltip(el, { container: 'body', customClass: 'action-tooltip' }));
    });
  }

  firstUpdated() {
    this._setupCanvas();
    this._setupResizeObserver();
    this._setupEventListeners();
    this._startRenderLoop();
    this._connectSSE();
    this._connectEphemeral();
  }

  /**
   * Publish a placement-hover ephemeral when our SNAPPED placement cell
   * changes. We snap to the nearest valid spawn — same as the local
   * shadow — so the remote ghost only ever lands on a legal placement
   * spot, not on whatever junk cell the cursor happens to be over.
   * @param {number} col
   * @param {number} row
   */
  _publishPlacementHoverIfChanged(col, row) {
    if (!this._ephemeral || !this._placementCharacterId) return;
    const nearest = this._findNearestSpawnPoint(col, row);
    if (!nearest) return;
    const last = this._lastPublishedHoverCell;
    if (last && last.col === nearest.col && last.row === nearest.row) return;
    this._lastPublishedHoverCell = { col: nearest.col, row: nearest.row };
    this._ephemeral.publish('placement-hover', {
      col: nearest.col,
      row: nearest.row,
      characterId: this._placementCharacterId,
    });
  }

  async _connectEphemeral() {
    if (!this.gameId) return;
    this._ephemeral = new EphemeralClient(this.gameId);
    await this._ephemeral.connect();
    this._unsubscribePlacementHover = this._ephemeral.subscribe('placement-hover', (msg) => {
      const data = msg.data || {};
      if (typeof data.col !== 'number' || typeof data.row !== 'number') return;
      interactionState.setRemotePlacementHover(msg.playerId, {
        col: data.col,
        row: data.row,
        characterId: data.characterId || '',
      });
    });
    // ephemeral:clear from the broadcaster on remote disconnect — drop
    // any hover we were rendering for that player.
    this._unsubscribeEphemeralClear = this._ephemeral.subscribe('*', (msg) => {
      interactionState.clearRemotePlacementHover(msg.playerId);
    });
  }

  _loadSavedState() {
    // Load viewport state from localStorage. Skipped in spectator mode so
    // every spectate session starts with a fresh zoom-to-fit on the lobby
    // and so spectator panning doesn't clobber the player's saved viewport.
    const viewportKey = `d3-gamemap-viewport-${this.gameId}`;
    const savedViewport = this.spectator ? null : localStorage.getItem(viewportKey);
    if (savedViewport) {
      try {
        const { zoom, panX, panY } = JSON.parse(savedViewport);
        viewState.setZoom(zoom);
        viewState.setPan(panX, panY);
        this._initialCenteringDone = true; // Don't auto-center if user had a saved viewport
      } catch (e) {
        console.warn('Failed to parse saved viewport state:', e);
      }
    }

    // Load drawer state
    const savedDrawerOpen = localStorage.getItem('d3-gamemap-player-drawerOpen');
    if (savedDrawerOpen !== null) {
      this._drawerOpen = savedDrawerOpen === 'true';
    }
    const savedDrawerView = localStorage.getItem('d3-gamemap-player-drawerActiveView');
    if (savedDrawerView) {
      this._drawerActiveView = savedDrawerView;
    }

    // Load equipment-screen open state so the manager survives a refresh.
    // Scoped to gameId — having the manager auto-open in game B because
    // the player left it open in game A would be surprising.
    const savedEquipOpen = localStorage.getItem(`d3-gamemap-equipmentScreenOpen-${this.gameId}`);
    if (savedEquipOpen !== null) {
      this._equipmentScreenOpen = savedEquipOpen === 'true';
    }

    // Load the per-game "Re-equip after throw" preference. Default true
    // when the player hasn't toggled it; on toggle we write the explicit
    // value, so unchecking it persists across reloads.
    const savedReEquip = localStorage.getItem(`d3-gamemap-reEquipThrown-${this.gameId}`);
    if (savedReEquip !== null) {
      this._reEquipThrownPref = savedReEquip === 'true';
    }

    // Load info drawer messages. Skipped in spectator mode so the
    // staff replay starts with an empty drawer instead of inheriting
    // the player's first-person history.
    const savedInfoMessages = this.spectator ? null : localStorage.getItem(`d3-gamemap-info-${this.gameId}`);
    if (savedInfoMessages) {
      try {
        const parsed = JSON.parse(savedInfoMessages);
        this._infoDrawerMessages = parsed.messages || [];
        this._infoMessageCounter = parsed.counter || 0;
      } catch (e) {
        console.warn('Failed to parse saved info drawer messages:', e);
      }
    }

    // Load showWalls preference
    const savedShowWalls = localStorage.getItem('d3-gamemap-player-showWalls');
    if (savedShowWalls !== null) {
      this._showWalls = savedShowWalls === 'true';
    }
  }

  _saveViewportState() {
    if (this.spectator) return;
    const viewportKey = `d3-gamemap-viewport-${this.gameId}`;
    const state = {
      zoom: viewState.zoom,
      panX: viewState.panX,
      panY: viewState.panY,
    };
    try {
      localStorage.setItem(viewportKey, JSON.stringify(state));
    } catch {
      // localStorage full - non-critical, skip
    }
  }

  _handleVisibilityChange() {
    const vis = mapState.mapVisibility;
    if (vis && vis !== this._lastVisibilityRef && vis.gridCells) {
      this._lastVisibilityRef = vis;
      const cellsToLoad = vis.gridCells.filter((c) => c.illumination !== 'none' || c.visibility === 'adjacent');
      if (cellsToLoad.length > 0) {
        loadTilesForCells(cellsToLoad);
      }
    }
  }

  _updateMusic() {
    if (mapState.waitingRoom?.isActive) {
      musicManager.switchTrack('waiting-room');
    } else if (mapState.turnState?.combatTime) {
      musicManager.switchTrack('combat');
    } else {
      musicManager.switchTrack('exploration');
    }
  }

  _checkPlacementMode() {
    if (!this.currentUserId) return;

    // Skip placement mode when waiting room is active (PCs are auto-placed at tavern seats)
    if (mapState.waitingRoom?.isActive) {
      this._isPlacementMode = false;
      this._isWaitingForOtherPlayers = false;
      if (!this._initialCenteringDone && this._canvasWidth > 0) {
        this._initialCenteringDone = true;
        this._centerOnWaitingRoomSeats();
      }
      return;
    }

    // Spectator: zoom-to-fit the adventure map once after the lobby ends.
    // The player flow handles its own centering via the placement / first-
    // load branches below, so we short-circuit here to keep that untouched.
    if (this.spectator) {
      if (!this._spectatorMapCentered && this._canvasWidth > 0) {
        this._spectatorMapCentered = true;
        this._zoomToFitMap();
      }
      return;
    }

    // Check if any of the current user's characters need placement
    const myCharacters = mapState.playerCharacters.filter((pc) => pc.playerId === this.currentUserId);
    const needsPlacement = myCharacters.some((c) => c.col === undefined || c.row === undefined);

    if (needsPlacement && mapState.pcStartingPositions.length > 0) {
      const charNeedingPlacement = myCharacters.find((c) => c.col === undefined || c.row === undefined);
      if (charNeedingPlacement && !this._isPlacementMode) {
        this._isPlacementMode = true;
        this._placementCharacterId = charNeedingPlacement.id;

        const validPositions = mapState.pcStartingPositions
          .filter((p) => p.col !== undefined && p.row !== undefined)
          .map((p) => ({ col: /** @type {number} */ (p.col), row: /** @type {number} */ (p.row) }));
        interactionState.startPlacement(charNeedingPlacement.id, validPositions);

        // Initialize shadow token at the middle still-available spawn point.
        const centerSpawn = validPositions[Math.floor(validPositions.length / 2)];
        if (centerSpawn) {
          interactionState.setShadowToken({
            col: centerSpawn.col,
            row: centerSpawn.row,
            character: charNeedingPlacement,
          });
          this._publishPlacementHoverIfChanged(centerSpawn.col, centerSpawn.row);
        }

        // Center viewport on starting positions
        this._centerOnStartingPositions();
      } else if (charNeedingPlacement && this._isPlacementMode) {
        // Already placing — another PC's placement removes cells from
        // mapState.pcStartingPositions (#235). Refresh validPositions so
        // the highlighted squares shrink in real time.
        const validPositions = mapState.pcStartingPositions
          .filter((p) => p.col !== undefined && p.row !== undefined)
          .map((p) => ({ col: /** @type {number} */ (p.col), row: /** @type {number} */ (p.row) }));
        interactionState.setPlacementValidPositions(validPositions);
      }
    } else if (this._isPlacementMode) {
      // Placement complete - center on placed character
      this._isPlacementMode = false;
      this._placementCharacterId = null;
      interactionState.endPlacement();
      this._initialCenteringDone = true;
      this._handleCenterOnTokens();
    } else if (!this._initialCenteringDone && myCharacters.length > 0 && this._canvasWidth > 0) {
      // First load with character already placed - center on them
      const hasPosition = myCharacters.some((c) => c.col !== undefined && c.row !== undefined);
      if (hasPosition) {
        this._initialCenteringDone = true;
        this._handleCenterOnTokens();
      }
    }

    // Check if waiting for other players
    this._isWaitingForOtherPlayers = this._computeWaitingForOtherPlayers(needsPlacement);
  }

  /**
   * @param {boolean} currentUserNeedsPlacement
   * @returns {boolean}
   */
  _computeWaitingForOtherPlayers(currentUserNeedsPlacement) {
    if (this._isPlacementMode || currentUserNeedsPlacement) {
      return false;
    }
    // Waiting if there are invited users who haven't joined
    if (mapState.invitedUsers.length > 0) {
      return true;
    }
    // Waiting if other players' characters need placement
    const otherChars = mapState.playerCharacters.filter((pc) => pc.playerId !== this.currentUserId);
    return otherChars.some((c) => c.col === undefined || c.row === undefined);
  }

  _centerOnStartingPositions() {
    const positions = mapState.pcStartingPositions;
    if (positions.length === 0 || this._canvasWidth === 0) return;

    const mapWidth = mapState.gridCols * mapState.gridSize;
    const mapHeight = mapState.gridRows * mapState.gridSize;
    const mapFitsInViewport =
      mapWidth > 0 && mapHeight > 0 && mapWidth < this._canvasWidth && mapHeight < this._canvasHeight;

    if (mapFitsInViewport) {
      // Small map: zoom up to 200% to fill the screen, then center the whole map
      const maxZoomX = this._canvasWidth / mapWidth;
      const maxZoomY = this._canvasHeight / mapHeight;
      const optimalZoom = Math.min(maxZoomX, maxZoomY, 2.0);
      const zoomedMapWidth = mapWidth * optimalZoom;
      const zoomedMapHeight = mapHeight * optimalZoom;
      // Set pan before zoom to avoid interference
      viewState.setPan((this._canvasWidth - zoomedMapWidth) / 2, (this._canvasHeight - zoomedMapHeight) / 2);
      viewState.setZoom(optimalZoom);
    } else {
      // Large map: center on the geometric center of spawn points
      let sumCol = 0;
      let sumRow = 0;
      for (const pos of positions) {
        sumCol += pos.col || 0;
        sumRow += pos.row || 0;
      }
      const centerCol = sumCol / positions.length;
      const centerRow = sumRow / positions.length;

      const centerX = (centerCol + 0.5) * mapState.gridSize;
      const centerY = (centerRow + 0.5) * mapState.gridSize;

      const newPanX = this._canvasWidth / 2 - centerX * viewState.zoom;
      const newPanY = this._canvasHeight / 2 - centerY * viewState.zoom;

      viewState.setPan(newPanX, newPanY);
    }
  }

  _zoomToFitMap() {
    if (this._canvasWidth === 0) return;
    const mapWidth = mapState.gridCols * mapState.gridSize;
    const mapHeight = mapState.gridRows * mapState.gridSize;
    if (mapWidth === 0 || mapHeight === 0) return;

    const zoomX = this._canvasWidth / mapWidth;
    const zoomY = this._canvasHeight / mapHeight;
    const zoom = Math.min(zoomX, zoomY, 4.0);

    const zoomedW = mapWidth * zoom;
    const zoomedH = mapHeight * zoom;
    viewState.setPan((this._canvasWidth - zoomedW) / 2, (this._canvasHeight - zoomedH) / 2);
    viewState.setZoom(zoom);
  }

  _centerOnWaitingRoomSeats() {
    const seats = mapState.waitingRoom?.seats;
    if (!seats || seats.length === 0 || this._canvasWidth === 0) return;

    // Calculate bounding box of all seat positions
    let minCol = Infinity,
      maxCol = -Infinity;
    let minRow = Infinity,
      maxRow = -Infinity;
    for (const seat of seats) {
      minCol = Math.min(minCol, seat.position.col);
      maxCol = Math.max(maxCol, seat.position.col);
      minRow = Math.min(minRow, seat.position.row);
      maxRow = Math.max(maxRow, seat.position.row);
    }

    // Add padding (in grid cells) around seats for context
    const padding = 3;
    minCol = Math.max(0, minCol - padding);
    maxCol = Math.min(mapState.gridCols - 1, maxCol + padding);
    minRow = Math.max(0, minRow - padding);
    maxRow = Math.min(mapState.gridRows - 1, maxRow + padding);

    // Calculate region size in world pixels
    const regionWidth = (maxCol - minCol + 1) * mapState.gridSize;
    const regionHeight = (maxRow - minRow + 1) * mapState.gridSize;

    // Zoom to fit the seat region in the viewport
    const zoomX = this._canvasWidth / regionWidth;
    const zoomY = this._canvasHeight / regionHeight;
    const zoom = Math.min(zoomX, zoomY, 2.0);

    // Center on the middle of the seat region
    const centerX = (minCol + (maxCol - minCol + 1) / 2) * mapState.gridSize;
    const centerY = (minRow + (maxRow - minRow + 1) / 2) * mapState.gridSize;

    viewState.setPan(this._canvasWidth / 2 - centerX * zoom, this._canvasHeight / 2 - centerY * zoom);
    viewState.setZoom(zoom);
  }

  _emitSnapshot() {
    const snapshot = {
      hook: mapState.hook,
      objective: mapState.objective,
      adventureCompleted: mapState.adventureCompleted,
      turnState: mapState.turnState,
      characters: mapState.playerCharacters,
      players: mapState.gamePlayers,
      waitingRoom: mapState.waitingRoom,
    };
    this.dispatchEvent(
      new CustomEvent('snapshot-updated', {
        detail: snapshot,
        bubbles: true,
        composed: true,
      }),
    );
  }

  _setupCanvas() {
    const container = this.shadowRoot?.querySelector('.game-map-container');
    const canvas = this.shadowRoot?.querySelector('canvas');
    if (!container || !canvas) return;

    // Initial size
    const rect = container.getBoundingClientRect();
    this._canvasWidth = rect.width;
    this._canvasHeight = rect.height;
    canvas.width = rect.width;
    canvas.height = rect.height;

    // Store references
    this._canvas = canvas;
    this._ctx = canvas.getContext('2d');
    this._container = container;
  }

  _setupResizeObserver() {
    const container = this._container;
    if (!container) return;

    this._resizeObserver = new ResizeObserver(() => {
      // Debounce resize updates to prevent canvas clearing during drawer
      // CSS transitions. The canvas stays at its original size throughout
      // the transition, then resizes once at the end.
      if (this._resizeTimeout !== null) {
        clearTimeout(this._resizeTimeout);
      }
      this._resizeTimeout = setTimeout(() => {
        this._updateCanvasSize();
      }, 16);
    });

    this._resizeObserver.observe(container);
  }

  _updateCanvasSize() {
    const container = this._container;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const newWidth = Math.floor(rect.width);
    const newHeight = Math.floor(rect.height);

    if (newWidth > 0 && newHeight > 0) {
      this._canvasWidth = newWidth;
      this._canvasHeight = newHeight;
    }
  }

  _cleanupResizeObserver() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
    if (this._resizeTimeout !== null) {
      clearTimeout(this._resizeTimeout);
      this._resizeTimeout = null;
    }
  }

  _setupEventListeners() {
    const canvas = this._canvas;
    if (!canvas) return;

    if (platformState.isMobile) {
      this._touchController = new TouchController(canvas, {
        onPan: (dx, dy) => {
          viewState.setPan(viewState.panX + dx, viewState.panY + dy);
        },
        onPinchZoom: (scaleDelta, centerX, centerY) => {
          const rect = canvas.getBoundingClientRect();
          const cx = centerX - rect.left;
          const cy = centerY - rect.top;
          const oldZoom = viewState.zoom;
          const newZoom = Math.min(Math.max(oldZoom * scaleDelta, viewState.minZoom), viewState.maxZoom);
          // Zoom toward pinch center
          const panX = cx - (cx - viewState.panX) * (newZoom / oldZoom);
          const panY = cy - (cy - viewState.panY) * (newZoom / oldZoom);
          viewState.setZoom(newZoom);
          viewState.setPan(panX, panY);
        },
        onTap: (clientX, clientY) => {
          // In placement mode, update shadow first then place
          if (this._isPlacementMode && this._placementCharacterId) {
            const worldPos = this._screenToWorld(clientX, clientY);
            const col = Math.floor(worldPos.x / mapState.gridSize);
            const row = Math.floor(worldPos.y / mapState.gridSize);
            this._updatePlacementShadow(col, row);
            const shadow = interactionState.placement.shadowToken;
            if (shadow && interactionState.isValidPlacementPosition(shadow.col, shadow.row)) {
              this.submitAction({
                type: 'pc:set-position',
                characterId: this._placementCharacterId,
                col: shadow.col,
                row: shadow.row,
                direction: 'down',
              });
            }
            return;
          }
          // Synthesize a click via the existing mouse handler
          this._handleMouseDown(/** @type {*} */ ({ clientX, clientY, button: 0 }));
          this._handleMouseUp(/** @type {*} */ ({ clientX, clientY, button: 0 }));
        },
        onDragStart: (clientX, clientY) => {
          if (this._isPlacementMode) {
            const worldPos = this._screenToWorld(clientX, clientY);
            const col = Math.floor(worldPos.x / mapState.gridSize);
            const row = Math.floor(worldPos.y / mapState.gridSize);
            this._updatePlacementShadow(col, row);
            this._touchDragMode = 'placement';
            return;
          }

          // Check if drag started on the user's own PC that can move
          if (!this._isPlacementMode && this._isMyTurn() && this._getMovementRemaining() > 0) {
            const worldPos = this._screenToWorld(clientX, clientY);
            const hit = this._findPlaceableAtPosition(worldPos.x, worldPos.y);
            if (hit && hit.kind === 'pc') {
              const isMyPC = mapState.playerCharacters.find((c) => c.id === hit.id)?.playerId === this.currentUserId;
              if (isMyPC) {
                const pc = mapState.playerCharacters.find((c) => c.id === hit.id);
                if (pc && pc.col !== undefined && pc.row !== undefined) {
                  const startPos = cellToPixelCenter({ col: pc.col, row: pc.row }, mapState.gridSize);
                  interactionState.startMovement({ kind: 'pc', id: pc.id }, startPos);
                  interactionState.select({ type: 'token', id: pc.id });
                  this._touchDragMode = 'movement';
                  return;
                }
              }
            }
          }

          this._touchDragMode = 'pan';
          this._lastTouchPanX = clientX;
          this._lastTouchPanY = clientY;
        },
        onDragMove: (clientX, clientY) => {
          if (this._touchDragMode === 'placement') {
            const worldPos = this._screenToWorld(clientX, clientY);
            const col = Math.floor(worldPos.x / mapState.gridSize);
            const row = Math.floor(worldPos.y / mapState.gridSize);
            this._updatePlacementShadow(col, row);
          } else if (this._touchDragMode === 'movement') {
            const worldPos = this._screenToWorld(clientX, clientY);
            const col = Math.floor(worldPos.x / mapState.gridSize);
            const row = Math.floor(worldPos.y / mapState.gridSize);
            const snappedPos = cellToPixelCenter({ col, row }, mapState.gridSize);
            const result = buildMovementTrail({
              trail: [...interactionState.movementTrail],
              newPos: snappedPos,
              walls: mapState.walls,
              openings: mapState.openings,
              tokens: /** @type {*} */ (this._getMapPlaceables()),
              draggedTokenId: interactionState.movingEntity?.id || '',
              gridSize: mapState.gridSize,
              maxMovementFeet: this._getMovementRemaining(),
              visibleCells: mapState.mapVisibility?.gridCells,
            });
            if (result) {
              interactionState.updateMovementTrail(result.trail, {
                col,
                row,
                direction: /** @type {import('../../types/jsdoc-types.js').TokenDirection} */ (
                  result.direction || 'down'
                ),
              });
            }
          } else if (this._touchDragMode === 'pan') {
            const dx = clientX - this._lastTouchPanX;
            const dy = clientY - this._lastTouchPanY;
            viewState.setPan(viewState.panX + dx, viewState.panY + dy);
            this._lastTouchPanX = clientX;
            this._lastTouchPanY = clientY;
          }
        },
        onDragEnd: (_clientX, _clientY) => {
          if (this._touchDragMode === 'placement' && this._placementCharacterId) {
            const shadow = interactionState.placement.shadowToken;
            if (shadow && interactionState.isValidPlacementPosition(shadow.col, shadow.row)) {
              this.submitAction({
                type: 'pc:set-position',
                characterId: this._placementCharacterId,
                col: shadow.col,
                row: shadow.row,
                direction: 'down',
              });
            }
          } else if (this._touchDragMode === 'movement') {
            const trail = interactionState.movementTrail;
            if (trail.length > 1) {
              this._submitMoveAction(trail);
            }
            interactionState.endMovement();
          }
          this._touchDragMode = null;
        },
      });
    } else {
      canvas.addEventListener('mousedown', this._handleMouseDown.bind(this));
      canvas.addEventListener('mouseup', this._handleMouseUp.bind(this));
      canvas.addEventListener('mousemove', this._handleMouseMove.bind(this));
      canvas.addEventListener('contextmenu', this._handleContextMenu.bind(this));
      canvas.addEventListener('wheel', this._handleWheel.bind(this), { passive: false });
      window.addEventListener('keydown', this._handleKeyDown.bind(this));
    }
  }

  _connectSSE() {
    if (!this.gameId || this.backendUrl === undefined) return;

    this._gameEvents.connect({
      entityId: this.gameId,
      backendUrl: this.backendUrl,
      // Pull a fresh token every time the controller needs one (REST
      // catch-up + each SSE connect/reconnect). authState.getAccessToken
      // transparently refreshes nearExpiry / tooOld tokens, so long
      // game sessions don't 403 across the 15-minute boundary.
      getAccessToken: () => authState.getAccessToken(),
      spectator: this.spectator,
      callbacks: {
        onCatchUpComplete: () => {
          // REST catch-up + saved-snapshot replay finished, so mapState
          // already reflects the current world. Reveal the map now rather
          // than waiting for the live SSE connection — a slow or failed
          // stream must not pin the loading overlay (#584: response
          // compression stalled SSE and hung the spinner indefinitely).
          this._revealMap();
        },
        onConnected: () => {
          console.debug('[GameMap] SSE connected');
          // Spectator sessions skip REST catch-up and build state purely
          // from the SSE replay, so onCatchUpComplete never fires for them
          // — reveal here too. Idempotent for the normal player path.
          this._revealMap();
        },
        onDisconnected: () => {
          console.debug('[GameMap] SSE disconnected');
        },
        onError: (/** @type {Event} */ error) => {
          console.error('[GameMap] SSE error:', error);
        },
      },
      eventProcessorCallbacks: {
        onAttackEvent: async (
          /** @type {{attackerType: string, attackerId: string, targetId: string, outcome: string, message: string, damage?: number}} */ info,
        ) => {
          // Floating text for attacks is server-driven via callouts — see
          // onCallouts below. We still emit npc-message for NPC attacks so the
          // info drawer's legacy channel keeps working.
          if (info.attackerType === 'npc') {
            this.dispatchEvent(
              new CustomEvent('npc-message', {
                bubbles: true,
                detail: { outcome: info.outcome, message: info.message },
              }),
            );
          }
        },
        onCallouts: (
          /** @type {Array<{anchor: {type: string, data: *}, text: string, color: string, delayMs: number, holdMs: number, fadeMs: number}>} */ callouts,
        ) => {
          this.showCallouts(callouts);
        },
        onNPCMessage: (/** @type {{outcome: string, message: string}} */ msg) => {
          this.dispatchEvent(
            new CustomEvent('npc-message', {
              bubbles: true,
              detail: msg,
            }),
          );
        },
        onCombatStarted: (
          /** @type {{initiatorType?: string, initiatorId?: string, initiatorName?: string, targetId?: string, targetName?: string, initiativeOrder?: Array<{actorId: string, playerId?: string, actorType?: string, name?: string, breakdown?: {d20Roll: number, modifiers?: Array<{value: number, source: string}>, total: number}}>}} */ combatState,
        ) => {
          // Show initiative reveal panel instead of a simple center announcement.
          // Returns a Promise that resolves when the animation completes.
          this._initiativeRevealState = combatState;

          // Emit initiative roll message to info drawer
          if (combatState?.initiativeOrder) {
            const myEntry = combatState.initiativeOrder.find((e) => e.playerId === this.currentUserId);
            if (myEntry?.breakdown) {
              const bd = myEntry.breakdown;
              let rollMsg = `Initiative: ${bd.d20Roll}`;
              for (const mod of bd.modifiers || []) {
                const sign = mod.value >= 0 ? '+' : '';
                rollMsg += ` ${sign} ${mod.value} (${mod.source})`;
              }
              rollMsg += ` = ${bd.total}`;
              this.dispatchEvent(
                new CustomEvent('initiative-rolled', {
                  bubbles: true,
                  detail: rollMsg,
                }),
              );
            }
          }

          return new Promise((resolve) => {
            this._initiativeRevealResolve = resolve;
          });
        },
        onCombatEnded: (/** @type {{ banner?: { title?: string } | null } | undefined} */ opts) => {
          // Server-rendered banner (#306) is now the source of truth.
          // The legacy fallback stays as a safety net for replayed
          // pre-#306 events that never had a banner attached.
          const title = opts?.banner?.title || 'Combat Ended';
          this.showCenterAnnouncement(title, 'combat-end');
        },
        onTurnAdvanced: (
          /** @type {{playerId?: string, actorType: string, name: string}} */ entry,
          /** @type {{ banner?: { title?: string } | null } | undefined} */ opts,
        ) => {
          // Server-rendered banner (#306): the server always attaches a
          // per-recipient title to combat:turn-started. The actor type
          // ('pc-turn' vs 'npc-turn') is still derived locally because
          // the server doesn't supply a styling category.
          const type = entry.actorType === 'pc' ? 'pc-turn' : 'npc-turn';
          let title = opts?.banner?.title || `${entry.name}'s Turn`;
          // Spectator override: the spectate SSE stream replays the
          // active player's per-recipient events, so a spectator
          // watching player A's turn would see "Your Turn". Swap in
          // the actor's name so the spectator sees who's actually
          // acting. Per-spectator fan-out would let us drop this
          // adapter; out of scope for #306.
          if (this.spectator && title === 'Your Turn') {
            title = `${entry.name}'s Turn`;
          }
          this.showCenterAnnouncement(title, type);
        },
        onWaitingRoomComplete: (/** @type {{ banner?: { title?: string } | null } | undefined} */ opts) => {
          const title = opts?.banner?.title || 'All Players Ready!';
          this.showCenterAnnouncement(title, 'combat-end');
        },
        onGameRestarted: (/** @type {{ restarterDisplayName?: string }} */ info) => {
          // The owner's POST response triggers _reloadForRestart first;
          // by the time SSE replays this event after reload, the notice
          // flag already exists and we skip to avoid a reload loop.
          const noticeKey = `game-restart-notice:${this.gameId}`;
          try {
            if (sessionStorage.getItem(noticeKey)) return;
          } catch {
            /* sessionStorage unavailable — fall through to reload */
          }
          this._reloadForRestart(info?.restarterDisplayName || 'the game owner');
        },
        onEventSummary: (/** @type {string} */ summary, /** @type {string} */ eventType) => {
          this._handleEventSummary(summary, eventType);
        },
      },
    });
  }

  _startRenderLoop() {
    const render = () => {
      this._renderCanvas();
      this._animationFrameId = requestAnimationFrame(render);
    };
    this._animationFrameId = requestAnimationFrame(render);
  }

  _stopRenderLoop() {
    if (this._animationFrameId) {
      cancelAnimationFrame(this._animationFrameId);
      this._animationFrameId = null;
    }
  }

  _renderCanvas() {
    const ctx = this._ctx;
    if (!ctx) return;

    const width = this._canvasWidth;
    const height = this._canvasHeight;
    if (width === 0 || height === 0) return;

    // Sync canvas buffer dimensions here (not in ResizeObserver) so the
    // clear that setting width/height causes is immediately followed by a
    // fresh render in the same frame — no blank-canvas flash during the
    // drawer slide transition.
    if (this._canvas && (this._canvas.width !== width || this._canvas.height !== height)) {
      this._canvas.width = width;
      this._canvas.height = height;
    }

    // Use render pipeline to draw everything
    renderGameMap({
      ctx,
      canvasWidth: width,
      canvasHeight: height,
      currentUserId: this.currentUserId,
      showGrid: this._showGrid,
      showWalls: this._showWalls,
    });
  }

  // Mouse event handlers
  /** @param {MouseEvent} e */
  _handleMouseDown(e) {
    this._isMouseDown = true;
    this._lastMouseX = e.clientX;
    this._lastMouseY = e.clientY;

    if (e.button === 2) {
      // Right-click - start panning
      this._isRightMouseDown = true;
      this._isPanning = true;
      return;
    }

    if (this._mode === 'pan') {
      this._isPanning = true;
      return;
    }

    // Handle placement mode click - use shadow token's snapped position
    if (this._isPlacementMode && this._placementCharacterId) {
      const shadow = interactionState.placement.shadowToken;
      if (shadow && interactionState.isValidPlacementPosition(shadow.col, shadow.row)) {
        this.submitAction({
          type: 'pc:set-position',
          characterId: this._placementCharacterId,
          col: shadow.col,
          row: shadow.row,
          direction: 'down',
        });
      }
      return;
    }

    // Handle placeable selection/interaction
    const worldPos = this._screenToWorld(e.clientX, e.clientY);
    const hit = this._findPlaceableAtPosition(worldPos.x, worldPos.y);

    // Handle attack targeting mode
    if (interactionState.mode === 'attack-targeting') {
      // Area targeting (AoE spells) - click on a grid cell
      if (interactionState.areaTargeting.isActive) {
        const gridSize = mapState.gridSize;
        const col = Math.floor(worldPos.x / gridSize);
        const row = Math.floor(worldPos.y / gridSize);
        const isInRange = interactionState.areaTargeting.targetSquares.some((s) => s.col === col && s.row === row);
        if (isInRange) {
          this._executeAreaAction(col, row);
          return;
        }
        // Clicked outside range - cancel
        interactionState.clearAttackTargeting();
        this._pendingAction = null;
        return;
      }

      // Targeting a placeable
      if (hit !== null) {
        if (interactionState.attackTargeting.validTargetIds.includes(hit.id)) {
          interactionState.selectAttackTarget(hit.id);
          if (interactionState.isTargetingComplete()) {
            this._executeMultiTargetAction(interactionState.attackTargeting.selectedTargets);
          }
          return;
        }
        // Token in range but not in validTargetIds (wrong filter) —
        // fall through to empty-square handling on the cell.
      }

      // Cell click within range with no valid token target: route to
      // the empty-square attack flow (truly blind shot — confirm once
      // before dispatching unless the player has opted out). Only
      // available when the action allows it (weapon attacks +
      // spell-attack spells); save-based / auto-hit / friendly-only
      // actions like Magic Missile, Sleep, and Healing Word would
      // produce no meaningful result against an empty square.
      const allowsEmptySquare = !!(/** @type {*} */ (this._pendingAction?.targeting)?.allowsEmptySquare);
      const targetingFilter = /** @type {*} */ (this._pendingAction?.targeting)?.filter;
      const gridSize = mapState.gridSize;
      const col = Math.floor(worldPos.x / gridSize);
      const row = Math.floor(worldPos.y / gridSize);
      const inRange = (interactionState.attackTargeting.targetSquares || []).some(
        (s) => s.col === col && s.row === row,
      );
      if (inRange && targetingFilter === 'selfOrEmpty') {
        // Light cantrip on an empty cell: no blind-shot confirm — the
        // player is placing a free-standing light, not gambling on a
        // miss. Dispatch straight to the square path.
        this._executeSquareAction(col, row);
        return;
      }
      if (inRange && allowsEmptySquare) {
        this._maybeConfirmEmptySquareAttack(col, row);
        return;
      }
      interactionState.clearAttackTargeting();
      this._pendingAction = null;
      return;
    }

    // Check direction indicator click on the current user's own PC
    if (!this._isPlacementMode) {
      const myPCs = mapState.playerCharacters.filter((pc) => pc.playerId === this.currentUserId);
      for (const pc of myPCs) {
        if (pc.col === undefined || pc.row === undefined) continue;
        if (this._isPointInDirectionIndicator(worldPos, /** @type {*} */ (pc))) {
          if (this._isMyTurn()) {
            interactionState.startDirectionDrag(
              { kind: 'pc', id: pc.id },
              /** @type {TokenDirection} */ (pc.direction || 'down'),
            );
            interactionState.select({ type: 'token', id: pc.id });
            return;
          }
          break;
        }
      }
    }

    if (hit !== null) {
      // Check if this is the current user's PC and they can move
      const isMyPC =
        hit.kind === 'pc' && mapState.playerCharacters.find((c) => c.id === hit.id)?.playerId === this.currentUserId;
      const isMyTurn = this._isMyTurn();
      const hasMovement = this._getMovementRemaining() > 0;

      if (isMyPC && isMyTurn && hasMovement && !this._isPlacementMode) {
        const pc = mapState.playerCharacters.find((c) => c.id === hit.id);
        if (pc && pc.col !== undefined && pc.row !== undefined) {
          const startPos = cellToPixelCenter({ col: pc.col, row: pc.row }, mapState.gridSize);
          interactionState.startMovement({ kind: 'pc', id: pc.id }, startPos);
          interactionState.select({ type: 'token', id: pc.id });
          return;
        }
      }

      interactionState.select({ type: 'token', id: hit.id });
    } else {
      interactionState.clearSelection();
    }
  }

  /**
   * @param {MouseEvent} e
   */
  _handleMouseUp(e) {
    this._isMouseDown = false;

    if (e.button === 2) {
      this._isRightMouseDown = false;
    }

    // Handle direction drag completion
    if (interactionState.isDraggingDirection && interactionState.directionDragEntity) {
      const dragEntity = interactionState.directionDragEntity;
      const newDirection = interactionState.directionDragPreview;

      if (dragEntity.kind === 'pc') {
        const character = mapState.playerCharacters.find((c) => c.id === dragEntity.id);
        if (character && newDirection && newDirection !== character.direction) {
          this.submitAction({
            type: 'pc:rotate',
            characterId: character.id,
            direction: newDirection,
          });
          // Optimistically update direction to prevent flash before server response
          const charIndex = mapState.playerCharacters.findIndex((c) => c.id === dragEntity.id);
          if (charIndex >= 0) {
            const chars = [...mapState.playerCharacters];
            chars[charIndex] = /** @type {Character} */ ({ ...character, direction: newDirection });
            mapState.playerCharacters = chars;
          }
        }
      } else {
        // monster placement direction drag (editor-only normally)
        const placement = mapState.monsterPlacements.find((p) => p.id === dragEntity.id);
        if (placement && newDirection && newDirection !== placement.direction) {
          mapState.updateMonsterPlacement(dragEntity.id, {
            direction: /** @type {TokenDirection} */ (newDirection),
          });
        }
      }

      interactionState.endDirectionDrag();
      interactionState.clearSelection();
      if (this._canvas) this._canvas.style.cursor = '';
      this._showRotateCursor = false;
      this.requestUpdate();
      return;
    }

    // Handle movement end before clearing panning
    if (interactionState.isMoving) {
      const trail = interactionState.movementTrail;
      if (trail.length > 1) {
        this._submitMoveAction(trail);
      }
      interactionState.endMovement();
      this._isPanning = false;
      return;
    }

    if (this._isPanning) {
      this._isPanning = false;
    }
  }

  /**
   * @param {MouseEvent} e
   */
  _handleMouseMove(e) {
    const worldPos = this._screenToWorld(e.clientX, e.clientY);
    this._currentGridCol = Math.floor(worldPos.x / mapState.gridSize);
    this._currentGridRow = Math.floor(worldPos.y / mapState.gridSize);

    if (this._isPanning) {
      const dx = e.clientX - this._lastMouseX;
      const dy = e.clientY - this._lastMouseY;
      viewState.setPan(viewState.panX + dx, viewState.panY + dy);
    }

    this._lastMouseX = e.clientX;
    this._lastMouseY = e.clientY;

    // Handle direction drag
    if (interactionState.isDraggingDirection && interactionState.directionDragEntity) {
      const dragEntity = interactionState.directionDragEntity;
      const placeable =
        dragEntity.kind === 'pc'
          ? mapState.playerCharacters.find((c) => c.id === dragEntity.id)
          : mapState.monsterPlacements.find((p) => p.id === dragEntity.id);
      if (!placeable || placeable.col === undefined || placeable.row === undefined) return;
      const gridSize = mapState.gridSize;
      const tokenCenterX = placeable.col * gridSize + gridSize / 2;
      const tokenCenterY = placeable.row * gridSize + gridSize / 2;

      const dx = worldPos.x - tokenCenterX;
      const dy = worldPos.y - tokenCenterY;
      const angle = Math.atan2(dy, dx);

      interactionState.updateDirectionDragPreview(this._getDirectionFromAngle(angle));

      if (this._canvas) this._canvas.style.cursor = 'none';
      this._showRotateCursor = true;
      this._rotateCursorX = e.clientX;
      this._rotateCursorY = e.clientY;
      this.requestUpdate();
      return;
    }

    // Handle movement trail building during drag
    if (interactionState.isMoving && !this._isPanning) {
      const snappedPos = cellToPixelCenter({ col: this._currentGridCol, row: this._currentGridRow }, mapState.gridSize);

      const result = buildMovementTrail({
        trail: [...interactionState.movementTrail],
        newPos: snappedPos,
        walls: mapState.walls,
        openings: mapState.openings,
        tokens: /** @type {*} */ (this._getMapPlaceables()),
        draggedTokenId: interactionState.movingEntity?.id || '',
        gridSize: mapState.gridSize,
        maxMovementFeet: this._getMovementRemaining(),
        visibleCells: mapState.mapVisibility?.gridCells,
      });

      if (result) {
        interactionState.updateMovementTrail(result.trail, {
          col: this._currentGridCol,
          row: this._currentGridRow,
          direction: /** @type {TokenDirection} */ (result.direction || 'down'),
        });
      }
    }

    // Handle placement mode - update shadow token and visibility
    if (this._isPlacementMode && this._placementCharacterId) {
      this._updatePlacementShadow(this._currentGridCol, this._currentGridRow);
      this._publishPlacementHoverIfChanged(this._currentGridCol, this._currentGridRow);
    }

    // Update hover state
    const hit = this._findPlaceableAtPosition(worldPos.x, worldPos.y);
    if (hit !== null) {
      interactionState.setHover(this._currentGridCol, this._currentGridRow, { type: 'token', id: hit.id });
    } else {
      interactionState.setHover(this._currentGridCol, this._currentGridRow);
    }

    // Update cursor
    if (interactionState.mode === 'attack-targeting' && this._canvas) {
      // Valid targets are shown with the target-icon overlay, so the cursor
      // just needs to reinforce clickability. Default elsewhere — no
      // crosshair spam.
      if (hit !== null && interactionState.attackTargeting.validTargetIds.includes(hit.id)) {
        this._canvas.style.cursor = 'pointer';
      } else {
        this._canvas.style.cursor = 'default';
      }
    } else if (this._canvas) {
      // Check for direction indicator hover on own PCs
      let hoveredDirectionIndicator = false;
      if (!this._isPlacementMode) {
        const myPCs = mapState.playerCharacters.filter((pc) => pc.playerId === this.currentUserId);
        for (const pc of myPCs) {
          if (pc.col === undefined || pc.row === undefined) continue;
          if (this._isPointInDirectionIndicator(worldPos, /** @type {*} */ (pc))) {
            hoveredDirectionIndicator = true;
            this._canvas.style.cursor = 'none';
            this._showRotateCursor = true;
            this._rotateCursorX = e.clientX;
            this._rotateCursorY = e.clientY;
            this.requestUpdate();
            break;
          }
        }
      }
      if (!hoveredDirectionIndicator) {
        this._canvas.style.cursor = '';
        if (this._showRotateCursor) {
          this._showRotateCursor = false;
          this.requestUpdate();
        }
      }
    }
  }

  /**
   * Find nearest starting position to the given grid coordinates
   * @param {number} col
   * @param {number} row
   * @returns {{ col: number, row: number } | null}
   */
  _findNearestSpawnPoint(col, row) {
    const positions = mapState.pcStartingPositions;
    if (positions.length === 0) return null;

    /** @type {{ col: number, row: number } | null} */
    let nearest = null;
    let minDist = Infinity;
    for (const pos of positions) {
      if (pos.col === undefined || pos.row === undefined) continue;
      const dx = pos.col - col;
      const dy = pos.row - row;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < minDist) {
        minDist = dist;
        nearest = { col: pos.col, row: pos.row };
      }
    }
    return nearest;
  }

  /**
   * Update shadow token position and visibility during placement
   * @param {number} col
   * @param {number} row
   */
  _updatePlacementShadow(col, row) {
    const nearest = this._findNearestSpawnPoint(col, row);
    if (!nearest) return;

    const currentShadow = interactionState.placement.shadowToken;
    const positionChanged = !currentShadow || currentShadow.col !== nearest.col || currentShadow.row !== nearest.row;

    if (!positionChanged) return;

    const character = mapState.playerCharacters.find((pc) => pc.id === this._placementCharacterId);
    if (!character) return;

    interactionState.setShadowToken({
      col: nearest.col,
      row: nearest.row,
      character,
    });

    // Visibility during placement is the union across all spawn points and is
    // sent once on /map/visibility — no per-spawn swap.
  }

  /**
   * @param {Event} e
   */
  _handleContextMenu(e) {
    e.preventDefault();
  }

  /**
   * @param {WheelEvent} e
   */
  _handleWheel(e) {
    e.preventDefault();

    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    const newZoom = Math.min(4.0, Math.max(0.25, viewState.zoom + delta));

    // Zoom toward mouse position
    if (!this._canvas) return;
    const rect = this._canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const worldX = (mouseX - viewState.panX) / viewState.zoom;
    const worldY = (mouseY - viewState.panY) / viewState.zoom;

    const newPanX = mouseX - worldX * newZoom;
    const newPanY = mouseY - worldY * newZoom;

    viewState.setZoom(newZoom);
    viewState.setPan(newPanX, newPanY);
  }

  /**
   * @param {KeyboardEvent} e
   */
  _handleKeyDown(e) {
    if (e.key === 'Escape') {
      if (interactionState.mode === 'attack-targeting') {
        interactionState.clearAttackTargeting();
        return;
      }
      if (interactionState.isMoving) {
        interactionState.endMovement();
        return;
      }
      this._gameMenuOpen = false;
      interactionState.clearSelection();
    }
  }

  /**
   * @param {number} screenX
   * @param {number} screenY
   * @returns {{ x: number, y: number }}
   */
  _screenToWorld(screenX, screenY) {
    const rect = this._canvas?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };

    const x = (screenX - rect.left - viewState.panX) / viewState.zoom;
    const y = (screenY - rect.top - viewState.panY) / viewState.zoom;
    return { x, y };
  }

  /**
   * Find a placeable (PC or monster placement) at the given world position.
   * @param {number} worldX
   * @param {number} worldY
   * @returns {{ kind: 'pc' | 'monster', id: string, index: number } | null}
   */
  _findPlaceableAtPosition(worldX, worldY) {
    const placeables = this._getMapPlaceables();
    const gridSize = mapState.gridSize;

    // Two-pass hit test: a PC standing in the same cell as a defeated
    // monster (a corpse) should be clickable through the corpse. Otherwise
    // a PC who walks onto a defeated enemy gets stuck — clicks land on the
    // monster behind/atop them and movement never starts.
    let firstHit = null;
    for (let i = placeables.length - 1; i >= 0; i--) {
      const p = placeables[i];
      if (!p) continue;
      const px = p.col * gridSize;
      const py = p.row * gridSize;
      const size = gridSize;
      if (worldX < px || worldX >= px + size || worldY < py || worldY >= py + size) continue;

      // Prefer any non-defeated, interactable placeable.
      if (!(p._kind === 'monster' && p.defeated)) {
        return { kind: p._kind, id: p.id, index: i };
      }
      if (firstHit === null) firstHit = { kind: p._kind, id: p.id, index: i };
    }
    return firstHit;
  }

  /**
   * Check if a world-space point is inside a token's direction indicator triangle
   * @param {{ x: number, y: number }} worldPos
   * @param {Token} token
   * @returns {boolean}
   */
  _isPointInDirectionIndicator(worldPos, token) {
    const gridSize = mapState.gridSize;
    const tokenRadius = gridSize / 2;

    // Token center
    const tokenX = token.col * gridSize + tokenRadius;
    const tokenY = token.row * gridSize + tokenRadius;

    // Triangle dimensions (same as token-renderer.js)
    const triangleWidth = tokenRadius / 3;
    const triangleHeight = tokenRadius / 5;

    /** @type {Record<string, number>} */
    const ROTATION_ANGLES = {
      down: 0,
      right: -Math.PI / 2,
      up: Math.PI,
      left: Math.PI / 2,
      'down-right': -Math.PI / 4,
      'down-left': Math.PI / 4,
      'up-right': (-3 * Math.PI) / 4,
      'up-left': (3 * Math.PI) / 4,
    };

    const direction = token.direction || 'down';
    const rotationAngle = ROTATION_ANGLES[direction];
    if (rotationAngle === undefined) throw new Error(`Unknown direction: ${direction}`);

    // Transform point to token's local coordinate system
    const dx = worldPos.x - tokenX;
    const dy = worldPos.y - tokenY;

    // Apply inverse rotation to point
    const cos = Math.cos(-rotationAngle);
    const sin = Math.sin(-rotationAngle);
    const localX = dx * cos - dy * sin;
    const localY = dx * sin + dy * cos;

    // Triangle vertices in local coordinates (pointing down initially)
    const tipX = 0;
    const tipY = tokenRadius;
    const baseLeftX = -triangleWidth / 2;
    const baseLeftY = tokenRadius - triangleHeight;
    const baseRightX = triangleWidth / 2;
    const baseRightY = tokenRadius - triangleHeight;

    // Check if point is inside triangle using barycentric coordinates
    const v0x = baseRightX - baseLeftX;
    const v0y = baseRightY - baseLeftY;
    const v1x = tipX - baseLeftX;
    const v1y = tipY - baseLeftY;
    const v2x = localX - baseLeftX;
    const v2y = localY - baseLeftY;

    const dot00 = v0x * v0x + v0y * v0y;
    const dot01 = v0x * v1x + v0y * v1y;
    const dot02 = v0x * v2x + v0y * v2y;
    const dot11 = v1x * v1x + v1y * v1y;
    const dot12 = v1x * v2x + v1y * v2y;

    const invDenom = 1 / (dot00 * dot11 - dot01 * dot01);
    const u = (dot11 * dot02 - dot01 * dot12) * invDenom;
    const v = (dot00 * dot12 - dot01 * dot02) * invDenom;

    return u >= 0 && v >= 0 && u + v <= 1;
  }

  /**
   * Convert an angle (from atan2) to a direction (cardinal or diagonal)
   * @param {number} angle - Angle in radians
   * @returns {TokenDirection}
   */
  _getDirectionFromAngle(angle) {
    let degrees = (angle * 180) / Math.PI;
    if (degrees < 0) degrees += 360;

    if (degrees >= 337.5 || degrees < 22.5) return 'right';
    if (degrees >= 22.5 && degrees < 67.5) return 'down-right';
    if (degrees >= 67.5 && degrees < 112.5) return 'down';
    if (degrees >= 112.5 && degrees < 157.5) return 'down-left';
    if (degrees >= 157.5 && degrees < 202.5) return 'left';
    if (degrees >= 202.5 && degrees < 247.5) return 'up-left';
    if (degrees >= 247.5 && degrees < 292.5) return 'up';
    return 'up-right';
  }

  /**
   * Check if it's the current user's turn
   * @returns {boolean}
   */
  _isMyTurn() {
    const ts = mapState.turnState;
    if (!ts) return false;
    if (ts.currentPlayerID === this.currentUserId) return true;
    const myChars = mapState.playerCharacters.filter((c) => c.playerId === this.currentUserId);
    return myChars.some((c) => ts.currentPlayerID === c.id);
  }

  /**
   * Get cursor class based on movement state and hover
   * @returns {string}
   */
  get _movementCursorClass() {
    if (interactionState.isMoving) return 'is-moving';
    if (this._mode === 'pan') return '';
    // Check if hovering over own PC
    const hovered = interactionState.hover.hoveredElement;
    if (hovered?.type !== 'token') return '';
    const pc = mapState.playerCharacters.find((c) => c.id === hovered.id);
    if (!pc || pc.playerId !== this.currentUserId) return '';
    if (!this._isMyTurn()) return '';
    return this._getMovementRemaining() > 0 ? 'can-move' : 'no-movement';
  }

  /**
   * Get the current user's player character.
   *
   * If a 'pre' bundled-swap is staged, overlays the post-swap fields the
   * server returned from preview-actions (actions, targetSquares,
   * equipmentBudget, ac, …) so targeting and the action menu render
   * against the post-swap state. The position / id / playerId stay from
   * the live snapshot. The overlay is dropped automatically when
   * _pendingEquipmentSwap clears (commit or cancel). #394.
   *
   * @returns {Character|undefined}
   */
  _getMyCharacter() {
    const live = mapState.playerCharacters.find((c) => c.playerId === this.currentUserId);
    if (!live) return live;
    const pending = this._pendingEquipmentSwap;
    if (pending && pending.phase === 'pre' && pending.previewCharacter) {
      // Object spread — the previewCharacter contains the post-swap shape
      // for everything derived (actions, targetSquares, …); identity and
      // map-position fields come from the live snapshot.
      return /** @type {any} */ ({ ...live, ...pending.previewCharacter });
    }
    return live;
  }

  /**
   * Get remaining movement in feet for the current user's active character
   * @returns {number}
   */
  _getMovementRemaining() {
    const ts = mapState.turnState;
    if (!ts?.turnResources) return 30;
    const resources = ts.turnResources[this.currentUserId];
    return resources?.movementRemaining ?? 30;
  }

  /**
   * Submit a pc:move action for the completed movement trail
   * @param {Array<{x: number, y: number}>} trail - Pixel-center positions
   */
  async _submitMoveAction(trail) {
    const movingEntity = interactionState.movingEntity;
    if (!movingEntity || movingEntity.kind !== 'pc') return;

    const character = mapState.playerCharacters.find((c) => c.id === movingEntity.id);
    if (!character) return;

    const gridSize = mapState.gridSize;
    const gridPath = trail.map((p) => ({
      col: Math.floor(p.x / gridSize),
      row: Math.floor(p.y / gridSize),
    }));

    const lastPos = trail[trail.length - 1];
    const secondToLast = trail[trail.length - 2];
    if (!lastPos) throw new Error('Expected trail to have at least one position');
    if (!secondToLast) throw new Error('Expected trail to have at least two positions');
    const destCol = Math.floor(lastPos.x / gridSize);
    const destRow = Math.floor(lastPos.y / gridSize);

    await this.submitAction({
      type: 'pc:move',
      characterId: character.id,
      path: gridPath,
      toCol: destCol,
      toRow: destRow,
    });
  }

  /**
   * Handle start-targeting event from turn-banner
   * @param {CustomEvent} e - detail is the action object
   */
  _handleStartTargeting(e) {
    const action = e.detail;
    const myChar = this._getMyCharacter();
    if (!myChar || myChar.col === undefined || myChar.row === undefined) return;

    // Spell-choices gate (Resistance, etc.): when the action declares
    // per-cast choices and we haven't yet resolved them, defer the targeting
    // flow and show the chooser modal. The submit handler stashes the
    // resolved values on `_pendingActionChoices` and re-invokes targeting.
    const spellChoices = action?.displayData?.spellChoices;
    if (Array.isArray(spellChoices) && spellChoices.length > 0 && !this._pendingActionChoices) {
      this._spellChoicesContext = { action, spellName: action.name || 'Spell', spellChoices };
      return;
    }

    const myAttackerId = myChar.id;

    // Range in grid cells. Server computed `myChar.targetSquares` covers
    // every cell within the longest reach across this character's
    // actions; we filter by the action's range here. Ranged attacks
    // include long-range cells (disadvantage applies; cursor variant
    // signals it).
    const rangeNormal = action.targeting?.range?.normal || 5;
    const rangeLong = action.targeting?.range?.long;
    const maxRange = Math.floor(rangeNormal / 5);
    const longRangeCells = rangeLong ? Math.floor(rangeLong / 5) : maxRange;
    const filter = action.targeting?.filter;
    const allCells =
      /** @type {Array<{col:number,row:number,distance:number,visible?:boolean,occupantTokenId?:string,occupantFilter?:string}>} */ (
        myChar.targetSquares || []
      );

    // Cells in range for this action. Filter cells by what's actually
    // clickable for THIS action so the colored highlight only paints
    // cells the player can land an attack on:
    //   - Out of range cells: never.
    //   - Cells with a wrong-filter occupant (friendly PC for a
    //     hostile attack, etc.): never.
    //   - Empty cells: only when the action allows empty-square
    //     targeting (#256: weapon attacks + spell-attack spells like
    //     Firebolt). Auto-hit / save-based / friendly-only spells
    //     (Magic Missile, Sleep, Healing Word) hide their highlight
    //     on empty cells since clicking them does nothing.
    const allowsEmptySquare = !!(/** @type {*} */ (action.targeting)?.allowsEmptySquare);
    const targetSquares = allCells
      .filter((c) => {
        if (c.distance > longRangeCells) return false;
        if (!c.occupantTokenId) return allowsEmptySquare;
        if (filter === 'hostile' && c.occupantFilter !== 'hostile') return false;
        if (filter === 'friendly' && c.occupantFilter !== 'friendly') return false;
        // selfOrEmpty (Light cantrip) accepts the caster's own cell and
        // empty cells only; reject every other occupant.
        if (filter === 'selfOrEmpty' && c.occupantTokenId !== myAttackerId) return false;
        return true;
      })
      .map((c) => ({ col: c.col, row: c.row }));

    this._pendingAction = action;

    // Area targeting for AoE spells (Sleep, Fireball, etc.)
    if (action.targeting?.type === 'area') {
      const areaShape = action.targeting.areaShape || 'sphere';
      const areaSize = action.targeting.areaSize || 5;
      // Self-origin cones (Burning Hands) point from the caster's cell.
      // The server's targetSquares list is computed for token/sphere
      // targeting and won't enumerate the cells we'd want to aim at —
      // for a cone we want every cell within length-cells Chebyshev
      // distance of the caster (excluding the caster cell itself) so
      // hovering snaps the cone direction. The cone's actual footprint
      // is computed at render time.
      let aoeTargetSquares = targetSquares;
      if (action.targeting?.range?.type === 'self' && action.targeting?.areaShape === 'cone') {
        const lengthCells = Math.floor(areaSize / 5);
        aoeTargetSquares = chebyshevDisk({ col: myChar.col, row: myChar.row }, lengthCells).filter(
          (c) => !(c.col === myChar.col && c.row === myChar.row),
        );
      }
      interactionState.startAreaTargeting(myAttackerId, action.id, aoeTargetSquares, areaShape, areaSize, filter);
      return;
    }

    // Token-targeting: pick cells with occupants matching the filter.
    // Placeholder ("shape in darkness") occupants flow through here too —
    // the server only fills in occupantTokenId for cells the attacker
    // can perceive (in-LOS or sensed-adjacent), so anything we see here
    // is fair game for token-targeting; the disadvantage / naming
    // adjustments happen in the attack handler.
    /** @type {Array<string>} */
    const validTargetIds = [];
    for (const c of allCells) {
      if (c.distance > longRangeCells) continue;
      if (!c.occupantTokenId) continue;
      if (filter === 'hostile' && c.occupantFilter !== 'hostile') continue;
      if (filter === 'friendly' && c.occupantFilter !== 'friendly') continue;
      // selfOrEmpty: only the caster is a valid token target.
      if (filter === 'selfOrEmpty' && c.occupantTokenId !== myAttackerId) continue;
      validTargetIds.push(c.occupantTokenId);
    }

    const longCells = action.targeting?.range?.long ? Math.floor(action.targeting.range.long / 5) : undefined;
    interactionState.startAttackTargeting(myAttackerId, action.id, validTargetIds, {
      maxTargets: action.targeting?.count || 1,
      allowRepeat: action.targeting?.allowRepeat || false,
      filter,
      rangeType: action.targeting?.range?.type,
      cursorBase: action.targeting?.cursorBase,
      rangeNormalCells: maxRange,
      rangeLongCells: longCells,
      attackerCol: myChar.col,
      attackerRow: myChar.row,
    });
    interactionState.attackTargeting.targetSquares = targetSquares;
  }

  /**
   * Handle execute-action event from turn-banner (no targeting needed)
   * @param {CustomEvent} e - detail is the action object
   */
  async _handleExecuteAction(e) {
    const action = e.detail;
    const myChar = this._getMyCharacter();
    if (!myChar) return;

    // Ready action (#266): instead of immediately submitting, open a
    // chooser modal so the player can pick which trigger to wait for.
    if (action.slug === 'ready') {
      this._readyChooserAction = action;
      return;
    }

    await this.submitAction({
      type: 'pc:act',
      characterId: myChar.id,
      actionId: action.id,
      targeting: { kind: 'self' },
    });
  }

  /**
   * Submit a Ready sub-choice (#266). Called from the chooser modal.
   * @param {string} actionId — the per-choice action id ("ready:wait-for-pc:<id>"
   *   or "ready:wait-for-melee-threat").
   */
  async _submitReadyChoice(actionId) {
    this._readyChooserAction = null;
    const myChar = this._getMyCharacter();
    if (!myChar) return;
    await this.submitAction({
      type: 'pc:act',
      characterId: myChar.id,
      actionId,
      targeting: { kind: 'self' },
    });
  }

  /**
   * Submit handler for the spell-choices modal. Stashes the resolved
   * choice map on `_pendingActionChoices`, closes the modal, and replays
   * the targeting flow with the same action.
   * @param {CustomEvent} e
   */
  _handleSpellChoicesSubmit(e) {
    const ctx = this._spellChoicesContext;
    if (!ctx) return;
    /** @type {Record<string,string>} */
    const choices = e.detail?.choices || {};
    this._pendingActionChoices = choices;
    const action = ctx.action;
    this._spellChoicesContext = null;
    this._handleStartTargeting(/** @type {CustomEvent} */ ({ detail: action }));
  }

  _handleSpellChoicesCancel() {
    this._spellChoicesContext = null;
    this._pendingActionChoices = null;
  }

  _renderSpellChoicesModal() {
    const ctx = this._spellChoicesContext;
    if (!ctx) return '';
    return html`
      <spell-choices-modal
        spell-name="${ctx.spellName}"
        .choices="${ctx.spellChoices}"
        @spell-choices-submit="${this._handleSpellChoicesSubmit}"
        @spell-choices-cancel="${this._handleSpellChoicesCancel}"
      ></spell-choices-modal>
    `;
  }

  _renderReadyChooserModal() {
    if (!this._readyChooserAction) return '';
    const myChar = this._getMyCharacter();
    const otherPCs = (mapState.playerCharacters || []).filter((c) => {
      if (!myChar || c.id === myChar.id) return false;
      const cond = /** @type {*} */ (c).conditions || {};
      return !cond.dead;
    });
    const combatTime = !!mapState.turnState?.combatTime;
    return html`
      <div class="modal-backdrop" @click="${() => (this._readyChooserAction = null)}">
        <div
          class="modal-menu"
          style="min-width: min(440px, 95vw); max-width: min(520px, 95vw);"
          @click="${/** @param {Event} e */ (e) => e.stopPropagation()}"
        >
          <div class="modal-header">
            <h3>Ready — choose a trigger</h3>
            <button class="modal-close" @click="${() => (this._readyChooserAction = null)}">&times;</button>
          </div>
          <div class="modal-content" style="display: flex; flex-direction: column; gap: 8px;">
            ${
              combatTime
                ? html`
                  <button
                    class="btn btn-secondary"
                    style="text-align: left;"
                    @click="${() => this._submitReadyChoice('ready:wait-for-melee-threat')}"
                  >
                    Wait for melee threat
                    <div style="font-size: 0.85em; opacity: 0.8;">
                      React when an enemy enters your reach mid-move.
                    </div>
                  </button>
                `
                : ''
            }
            ${
              otherPCs.length === 0
                ? html`<div style="opacity: 0.7; font-size: 0.9em;">No other living PCs to wait for.</div>`
                : otherPCs.map(
                    (pc) => html`
                    <button
                      class="btn btn-secondary"
                      style="text-align: left;"
                      @click="${() => this._submitReadyChoice(`ready:wait-for-pc:${pc.id}`)}"
                    >
                      Wait for ${pc.name}
                      <div style="font-size: 0.85em; opacity: 0.8;">
                        Hold your turn until ${pc.name} acts this round.
                      </div>
                    </button>
                  `,
                  )
            }
          </div>
        </div>
      </div>
    `;
  }

  /** @param {KeyboardEvent} event */
  _handleModalEscape(event) {
    if (event.key === 'Escape') {
      this._showActionsModal = false;
    }
  }

  // Action / bonus-action modal icon helpers. Moved from turn-banner so the
  // modal can render inside game-map-container (which is positioned relative
  // and excludes the side drawer-panel as a flex sibling). Modal lives here
  // instead of inside turn-banner's shadow DOM because position:fixed there
  // covers the whole viewport, ignoring the drawer.
  _renderModalSwordsIcon() {
    return html`
      <svg
        class="modal-icon-xl"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5" />
        <line x1="13" y1="19" x2="19" y2="13" />
        <line x1="16" y1="16" x2="20" y2="20" />
        <line x1="19" y1="21" x2="21" y2="19" />
        <polyline points="14.5 6.5 18 3 21 3 21 6 17.5 9.5" />
        <line x1="5" y1="14" x2="9" y2="18" />
        <line x1="7" y1="17" x2="4" y2="20" />
        <line x1="3" y1="19" x2="5" y2="21" />
      </svg>
    `;
  }

  _renderModalTargetIcon() {
    return html`
      <svg
        class="modal-icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="6" />
        <circle cx="12" cy="12" r="2" />
      </svg>
    `;
  }

  _renderModalFlameIcon() {
    return html`
      <svg
        class="modal-icon torch-action-icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path
          d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"
        />
      </svg>
    `;
  }

  _renderModalZapIcon() {
    return html`
      <svg
        class="modal-icon dash-action-icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    `;
  }

  _renderModalShieldIcon() {
    return html`
      <svg
        class="modal-icon dodge-action-icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path
          d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
        />
      </svg>
    `;
  }

  /** @param {{slug?: string, targeting?: {range?: {type?: string}}}} action */
  _getModalActionIcon(action) {
    if (action.slug === 'melee-weapon-attack') return this._renderModalSwordsIcon();
    if (action.slug === 'ranged-weapon-attack') return this._renderModalTargetIcon();
    if (action.slug === 'offhand-weapon-attack') {
      return action.targeting?.range?.type === 'ranged' ? this._renderModalTargetIcon() : this._renderModalSwordsIcon();
    }
    if (action.slug === 'initiate-combat') return this._renderModalSwordsIcon();
    if (action.slug === 'light-torch') return this._renderModalFlameIcon();
    if (action.slug === 'dash') return this._renderModalZapIcon();
    if (action.slug === 'dodge') return this._renderModalShieldIcon();
    return html``;
  }

  /** @param {import('../../types/jsdoc-types.js').CharacterAction & {targeting?: {type?: string}}} action */
  _handleModalActionClick(action) {
    this._showActionsModal = false;
    // Call the same handlers turn-banner used to fire via @execute-action /
    // @start-targeting events. Dispatching events here instead would bubble
    // *past* those listeners (they're bound on the child <turn-banner>, not
    // on this host), so we invoke them directly with an event-shaped detail.
    const detail = /** @type {*} */ ({ detail: action });
    if (!action.targeting || action.targeting.type === 'self') {
      this._handleExecuteAction(detail);
    } else {
      this._handleStartTargeting(detail);
    }
  }

  /**
   * Compute the modal-menu's max-width for `count` items.
   *
   * Use as few columns as possible to avoid wasting horizontal space:
   * stay in 1 column until the items would overflow the modal's available
   * height, then add a column at a time. Capped at 4 columns; beyond that
   * the modal-content scrolls.
   *
   * Estimates: ~60px per item (12+12 padding + ~22 line + 8px gap),
   * ~56px header, ~24px content padding, ~140px backdrop padding+chrome.
   * Each column is 260px min + 8px gap + share of 24px content padding +
   * ~1px border (rounded into the constant), capped at 95vw on narrow
   * viewports.
   */
  _renderActionsModal() {
    /** @type {Array<import('../../types/jsdoc-types.js').CharacterAction & {slug?: string, icon?: string, actionType?: string, actionSummary?: string, rulesSummary?: string, effects?: Array<{icon?: string, title: string, rulesSummary?: string}>, costs?: Array<{type?: string, label?: string}>, displayData?: {kind?: string, slotsRemaining?: number, slotsMax?: number, spellLevel?: number, spellSlotLevel?: number, usesRemaining?: number, usesMax?: number, freeCastsRemaining?: number, freeCastsMax?: number}}>} */
    const actions = /** @type {any} */ (this._getMyCharacter()?.actions || []);

    // Single alphabetized list. Action names start with their action type
    // (e.g. "Attack (Longsword)", "Dash") so alphabetic order keeps the
    // type-related entries adjacent without explicit grouping.
    const sorted = [...actions].sort((a, b) => a.name.localeCompare(b.name));

    // Three balanced columns at lg, two at md, one below. Layout uses
    // CSS grid with `grid-auto-flow: column` so items fill columns
    // top-to-bottom (preserves alphabetical reading order down each
    // column) AND items in the same visual row align in height.
    const colCount = window.innerWidth >= 992 ? 3 : window.innerWidth >= 768 ? 2 : 1;
    const rowCount = Math.max(1, Math.ceil(sorted.length / colCount));

    return html`
      <div class="modal-backdrop" @click="${() => (this._showActionsModal = false)}">
        <div
          class="modal-menu"
          style="min-width: min(992px, 95vw); max-width: min(1100px, 95vw);"
          @click="${/** @param {Event} e */ (e) => e.stopPropagation()}"
        >
          <div class="modal-header">
            <h3>Actions</h3>
            <button class="modal-close" @click="${() => (this._showActionsModal = false)}">&times;</button>
          </div>
          <div class="modal-content">
            <div
              class="actions-grid"
              style="grid-template-columns: repeat(${colCount}, 1fr); grid-template-rows: repeat(${rowCount}, auto);"
            >
              ${sorted.map((action) => this._renderActionCard(action))}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /** @param {any} action */
  /**
   * Confirm dialog shown when the player clicks an empty in-range cell
   * during attack-targeting. Yes submits a square-kind pc:act; Cancel
   * keeps targeting active. The "don't ask again" checkbox writes to
   * localStorage so future empty-square attacks dispatch immediately.
   */
  _renderEmptySquareConfirm() {
    const { col, row } = this._emptySquareConfirm;
    // Standalone overlay sized for a confirm dialog — doesn't reuse
    // the actions-modal layout (which is grid-shaped and top-anchored).
    const backdrop = `
      position: absolute; inset: 0;
      display: flex; align-items: center; justify-content: center;
      background: rgba(0, 0, 0, 0.45);
      z-index: 10001;
    `;
    const card = `
      width: min(420px, 92vw);
      background: rgba(0, 0, 0, 0.95);
      border: 1px solid rgba(255, 255, 255, 0.25);
      border-radius: 8px;
      color: white;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.8);
      pointer-events: auto;
    `;
    return html`
      <div style="${backdrop}" @click="${() => (this._emptySquareConfirm = null)}">
        <div style="${card}" @click="${/** @param {Event} e */ (e) => e.stopPropagation()}">
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 14px 20px; border-bottom: 1px solid rgba(255, 255, 255, 0.2);">
            <h3 style="margin: 0; font-size: 18px; font-weight: 600;">Attack empty square?</h3>
            <button
              type="button"
              @click="${() => (this._emptySquareConfirm = null)}"
              style="background: none; border: none; color: rgba(255, 255, 255, 0.7); font-size: 24px; line-height: 1; cursor: pointer; padding: 0 4px;"
              aria-label="Close"
            >&times;</button>
          </div>
          <div style="padding: 16px 20px;">
            <p style="margin: 0 0 14px; font-size: 14px; line-height: 1.45;">
              You're targeting an empty square at (${col}, ${row}). The attack
              will roll against any unseen creature there, or miss outright if
              nothing is in that square — either way the action is consumed.
            </p>
            <label style="display: flex; align-items: center; gap: 8px; margin-bottom: 16px; font-size: 13px;">
              <input type="checkbox" id="empty-square-suppress" />
              Don't ask again on this device
            </label>
            <div style="display: flex; justify-content: flex-end; gap: 8px;">
              <button
                type="button"
                class="btn btn-secondary"
                @click="${() => (this._emptySquareConfirm = null)}"
              >Cancel</button>
              <button
                type="button"
                class="btn btn-primary"
                @click="${() => this._confirmEmptySquareAttack()}"
              >Attack</button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  _confirmEmptySquareAttack() {
    if (!this._emptySquareConfirm) return;
    const { col, row } = this._emptySquareConfirm;
    const suppress = /** @type {HTMLInputElement|null} */ (this.renderRoot?.querySelector('#empty-square-suppress'));
    if (suppress?.checked) {
      localStorage.setItem(EMPTY_SQUARE_CONFIRM_DISMISSED_KEY, '1');
    }
    this._emptySquareConfirm = null;
    this._executeSquareAction(col, row);
  }

  _renderActionCard(action) {
    const dd = action.displayData;
    let slotsLine = '';
    if (dd?.kind === 'spell' && dd.freeCastsRemaining != null && dd.freeCastsMax != null) {
      // Per-rest slotless casts (Magic Initiate's L1 spell, etc.) — surface
      // an "N/M uses" indicator like class actions do, separate from the
      // slot-driven cast variant of the same spell.
      slotsLine = `${dd.freeCastsRemaining}/${dd.freeCastsMax} uses`;
    } else if (dd?.kind === 'spell' && dd.slotsRemaining != null) {
      slotsLine = `${dd.slotsRemaining}/${dd.slotsMax} lvl ${dd.spellSlotLevel ?? dd.spellLevel ?? '?'} slots`;
    } else if (dd?.kind === 'class' && dd.usesRemaining != null) {
      slotsLine = `${dd.usesRemaining}/${dd.usesMax} uses`;
    }
    const cost = action.costs?.[0];
    // Only call out non-default costs. The Action button's group label
    // already implies "Action"; we only need a badge when the cost is
    // unusual (bonus action, free, etc.).
    const showCostBadge = cost && cost.type !== 'action';
    /** @type {import('./action-card.js').ActionCardBadge[]} */
    const badges = [];
    if (showCostBadge) badges.push({ label: cost.label, tone: 'secondary' });
    if (action.effects?.length) {
      for (const effect of action.effects) {
        // Effect badges with rulesSummary get a Bootstrap tooltip; the
        // shared renderActionCard reads `rulesSummary` and emits the
        // data-bs-toggle attributes (matching the legacy inline render).
        badges.push({
          label: effect.title,
          tone: 'info',
          rulesSummary: effect.rulesSummary,
        });
      }
    }

    const card = renderActionCard({
      iconSlug: action.icon,
      title: action.name,
      description: action.description,
      summary: action.actionSummary,
      rulesSummary: action.rulesSummary,
      enabled: action.enabled,
      disabledReason: action.disabledReason,
      badges,
      bottomRowRight: slotsLine || undefined,
      onClick: () => this._handleModalActionClick(action),
    });

    if (dd?.kind === 'weapon' && dd.reEquipAvailable === true) {
      const checked = this._reEquipThrownPref;
      return html`
        <div class="action-cell-with-toggle">
          ${card}
          <label
            class="form-check re-equip-toggle"
            @click="${/** @param {Event} e */ (e) => e.stopPropagation()}"
          >
            <input
              class="form-check-input"
              type="checkbox"
              .checked="${checked}"
              @change="${
                /** @param {Event} e */ (e) => {
                  const next = /** @type {HTMLInputElement} */ (e.target).checked;
                  this._reEquipThrownPref = next;
                  localStorage.setItem(`d3-gamemap-reEquipThrown-${this.gameId}`, String(next));
                }
              }"
            />
            <span class="form-check-label">Re-equip after throw</span>
          </label>
        </div>
      `;
    }
    return card;
  }

  /**
   * Execute the pending area action at the given grid center
   * @param {number} col
   * @param {number} row
   */
  async _executeAreaAction(col, row) {
    const myChar = this._getMyCharacter();
    if (!myChar || !this._pendingAction) return;

    const action = this._pendingAction;
    interactionState.clearAttackTargeting();
    this._pendingAction = null;

    await this.submitAction({
      type: 'pc:act',
      characterId: myChar.id,
      actionId: action.id,
      targeting: { kind: 'area', center: { col, row } },
    });
  }

  /**
   * Send a square-targeted attack at (col, row). Used when the player
   * clicks an empty in-range cell while in attack-targeting mode — the
   * server resolves to a hidden creature there or to a clean miss.
   * @param {number} col
   * @param {number} row
   */
  async _executeSquareAction(col, row) {
    const myChar = this._getMyCharacter();
    if (!myChar || !this._pendingAction) return;

    const action = this._pendingAction;
    interactionState.clearAttackTargeting();
    this._pendingAction = null;

    /** @type {Record<string, any>} */
    const payload = {
      type: 'pc:act',
      characterId: myChar.id,
      actionId: action.id,
      targeting: { kind: 'square', center: { col, row } },
    };
    this._attachPendingPrefixSwap(payload, action);
    this._attachReEquipThrown(payload, action);

    try {
      await this.submitAction(payload);
    } finally {
      // Whether the server accepted or rejected the bundled change, the
      // staged swap is no longer pending — success means the server
      // committed it; failure means we surfaced the error and the user
      // can retry. Either way the optimistic state is cleared.
      if (payload.prefixEquipmentChanges || payload.postfixEquipmentChanges) {
        this._pendingEquipmentSwap = null;
      }
    }
  }

  /**
   * Open the empty-square confirm dialog (or skip it if the player has
   * disabled the warning) before submitting a square-targeted attack.
   * @param {number} col
   * @param {number} row
   */
  _maybeConfirmEmptySquareAttack(col, row) {
    if (localStorage.getItem(EMPTY_SQUARE_CONFIRM_DISMISSED_KEY) === '1') {
      this._executeSquareAction(col, row);
      return;
    }
    this._emptySquareConfirm = { col, row };
  }

  /**
   * Execute the pending targeted action against the selected target
   * @param {string} targetId - Token ID of the target
   */
  async _executeTargetedAction(targetId) {
    this._executeMultiTargetAction([targetId]);
  }

  /**
   * Execute the pending targeted action against the selected targets
   * @param {Array<string>} targetIds - Token IDs of the targets
   */
  async _executeMultiTargetAction(targetIds) {
    const myChar = this._getMyCharacter();
    if (!myChar || !this._pendingAction) return;

    const action = this._pendingAction;
    interactionState.clearAttackTargeting();
    this._pendingAction = null;

    /** @type {Record<string, any>} */
    const payload = {
      type: 'pc:act',
      characterId: myChar.id,
      actionId: action.id,
      targeting: { kind: 'token', targets: targetIds },
    };
    this._attachPendingPrefixSwap(payload, action);
    this._attachReEquipThrown(payload, action);
    if (this._pendingActionChoices) {
      payload.choices = this._pendingActionChoices;
    }

    try {
      await this.submitAction(payload);
    } finally {
      if (payload.prefixEquipmentChanges || payload.postfixEquipmentChanges) {
        this._pendingEquipmentSwap = null;
      }
      this._pendingActionChoices = null;
    }
  }

  /**
   * Accept a pending extra-attack offer (Cleave) — kick off targeting
   * mode using the server-provided valid target ids. When the player
   * clicks one, _executeMultiTargetAction submits a pc:act with the
   * extra-attack action id.
   * @param {*} offer - PendingExtraAttackOffer payload from the snapshot
   */
  _handleAcceptExtraAttackOffer(offer) {
    const myChar = this._getMyCharacter();
    if (!myChar || !offer) return;
    this._pendingAction = {
      id: `extra-attack:${offer.slug}`,
      slug: 'extra-attack',
      name: offer.name || 'Extra Attack',
      category: 'weapon',
    };

    // Build target squares from each valid target's current position so
    // the targeting overlay highlights them. Mirrors the targetSquares
    // shape compute_actions emits for normal weapon attacks.
    const validIds = offer.validTargetIds || [];
    const targetSquares = [];
    for (const id of validIds) {
      const placement = mapState.monsterPlacements?.find((p) => p.id === id);
      if (!placement) continue;
      targetSquares.push({
        col: placement.col,
        row: placement.row,
        distance: 1,
        visible: true,
        occupantTokenId: id,
        occupantFilter: 'hostile',
      });
    }

    interactionState.startAttackTargeting(myChar.id, `extra-attack:${offer.slug}`, validIds, {
      maxTargets: 1,
      cursorBase: 'swords',
      attackerCol: myChar.col,
      attackerRow: myChar.row,
    });
    interactionState.attackTargeting.targetSquares = targetSquares;
  }

  /**
   * Decline a pending extra-attack offer — submit a decline action so
   * the server clears the snapshot field. The dispatcher's implicit-
   * decline pass also clears it on any other action, so this is the
   * explicit Skip path.
   * @param {*} offer
   */
  _handleDeclineExtraAttackOffer(offer) {
    const myChar = this._getMyCharacter();
    if (!myChar || !offer) return;
    this.submitAction({
      type: 'pc:act',
      characterId: myChar.id,
      actionId: `decline-extra-attack:${offer.slug}`,
      targeting: { kind: 'self' },
    });
  }

  /**
   * If the player has staged a free Attack-bundled equipment swap and the
   * action is a (non-offhand) weapon attack, attach the staged changes
   * to the action payload so the server commits swap+attack atomically.
   * The phase ('pre' or 'post') chooses whether the swap rides as
   * `prefixEquipmentChanges` (apply BEFORE the attack — the picker's
   * "Unequip, then attack" branch) or `postfixEquipmentChanges` (apply
   * AFTER — the picker's "Attack, then unequip" branch).
   *
   * No-op for non-attack actions or when no swap is staged.
   * @param {Record<string, any>} payload
   * @param {any} action
   */
  _attachPendingPrefixSwap(payload, action) {
    if (!this._pendingEquipmentSwap) return;
    const slug = typeof action?.slug === 'string' ? action.slug : '';
    const isAttack = slug === 'melee-weapon-attack' || slug === 'ranged-weapon-attack';
    if (!isAttack) return;
    const { changes, phase } = this._pendingEquipmentSwap;
    if (phase === 'post') {
      payload.postfixEquipmentChanges = changes;
    } else {
      payload.prefixEquipmentChanges = changes;
    }
  }

  /**
   * If the action card exposed the "re-equip after throw" toggle AND the
   * player's saved preference is on, attach `reEquipThrown: true` to the
   * pc:act payload. The server's thrown-weapon post-attack pipeline reads
   * it to auto-equip a spare.
   * @param {Record<string, any>} payload
   * @param {any} action
   */
  _attachReEquipThrown(payload, action) {
    if (action?.displayData?.reEquipAvailable !== true) return;
    if (!this._reEquipThrownPref) return;
    payload.reEquipThrown = true;
  }

  // UI Event handlers
  _handleToggleSelectMode() {
    this._mode = this._mode === 'select' ? 'select' : 'select';
  }

  _handleTogglePanMode() {
    this._mode = this._mode === 'pan' ? 'select' : 'pan';
  }

  _handleZoomIn() {
    const newZoom = Math.min(4.0, viewState.zoom + 0.25);
    viewState.setZoom(newZoom);
  }

  _handleZoomOut() {
    const newZoom = Math.max(0.25, viewState.zoom - 0.25);
    viewState.setZoom(newZoom);
  }

  _handleResetZoom() {
    viewState.setZoom(1.0);
  }

  _handleToggleGrid() {
    this._showGrid = !this._showGrid;
  }

  async _handleCopySeed() {
    if (mapState.seed == null) return;
    try {
      await navigator.clipboard.writeText(String(mapState.seed));
    } catch (err) {
      console.error('[GameMap] Failed to copy seed:', err);
      return;
    }
    this._seedCopied = true;
    clearTimeout(this._seedCopiedTimer);
    this._seedCopiedTimer = setTimeout(() => {
      this._seedCopied = false;
    }, 1500);
  }

  _handleToggleWalls() {
    this._showWalls = !this._showWalls;
    localStorage.setItem('d3-gamemap-player-showWalls', String(this._showWalls));
  }

  _handleCenterOnTokens() {
    if (this._canvasWidth === 0) return;
    const mapWidth = mapState.gridCols * mapState.gridSize;
    const mapHeight = mapState.gridRows * mapState.gridSize;
    if (mapWidth === 0 || mapHeight === 0) return;

    // Zoom so the map fills the viewport in both dimensions (cover, not
    // contain). Picking the larger ratio means one dimension overflows and
    // is pannable. Capped at 4× to match the app-wide max zoom.
    const optimalZoom = Math.min(Math.max(this._canvasWidth / mapWidth, this._canvasHeight / mapHeight), 4.0);
    viewState.setZoom(optimalZoom);

    // Pan so the player's tokens are centered when possible. If the map
    // doesn't overflow in a given axis (i.e. it fits at this zoom), center
    // the map itself on that axis instead of the tokens — otherwise we'd
    // pan into empty space.
    const myCharacters = mapState.playerCharacters.filter((c) => c.playerId === this.currentUserId);
    let targetX = mapWidth / 2;
    let targetY = mapHeight / 2;
    const positioned = myCharacters.filter((c) => c.col !== undefined && c.row !== undefined);
    if (positioned.length > 0) {
      let sumCol = 0,
        sumRow = 0;
      for (const c of positioned) {
        sumCol += c.col;
        sumRow += c.row;
      }
      targetX = (sumCol / positioned.length + 0.5) * mapState.gridSize;
      targetY = (sumRow / positioned.length + 0.5) * mapState.gridSize;
    }

    const zoomedMapWidth = mapWidth * optimalZoom;
    const zoomedMapHeight = mapHeight * optimalZoom;
    // Per-axis: if the map fits in viewport, center the map; otherwise
    // center on the target (player) and clamp so we don't pan past the
    // map edges leaving empty space.
    const panX =
      zoomedMapWidth <= this._canvasWidth
        ? (this._canvasWidth - zoomedMapWidth) / 2
        : Math.min(0, Math.max(this._canvasWidth - zoomedMapWidth, this._canvasWidth / 2 - targetX * optimalZoom));
    const panY =
      zoomedMapHeight <= this._canvasHeight
        ? (this._canvasHeight - zoomedMapHeight) / 2
        : Math.min(0, Math.max(this._canvasHeight - zoomedMapHeight, this._canvasHeight / 2 - targetY * optimalZoom));
    viewState.setPan(panX, panY);
  }

  _handleCloseDrawer() {
    this._drawerOpen = false;
    this._drawerActiveView = null;
    localStorage.setItem('d3-gamemap-player-drawerOpen', 'false');
  }

  /**
   * @param {string} view
   */
  _handleToggleDrawerView(view) {
    if (this._drawerActiveView === view && this._drawerOpen) {
      this._drawerOpen = false;
      this._drawerActiveView = null;
    } else {
      this._drawerOpen = true;
      this._drawerActiveView = view;
    }
    if (view === 'info') {
      this._infoBadge = false;
    }
    localStorage.setItem('d3-gamemap-player-drawerOpen', String(this._drawerOpen));
    localStorage.setItem('d3-gamemap-player-drawerActiveView', this._drawerActiveView || '');
  }

  /**
   * Handle a new event summary from the event processor
   * @param {string} summary
   * @param {string} eventType
   */
  _handleEventSummary(summary, eventType) {
    const msg = {
      id: ++this._infoMessageCounter,
      summary,
      eventType,
      timestamp: Date.now(),
      highlighted: true,
    };
    this._infoDrawerMessages = [...this._infoDrawerMessages, msg];
    this._saveInfoDrawerMessages();

    // Remove highlight after animation completes
    setTimeout(() => {
      this._infoDrawerMessages = this._infoDrawerMessages.map((m) =>
        m.id === msg.id ? { ...m, highlighted: false } : m,
      );
    }, 1500);

    if (!this._drawerOpen) {
      // Drawer is closed: open it to 'info' view
      this._drawerOpen = true;
      this._drawerActiveView = 'info';
      this._infoBadge = false;
      localStorage.setItem('d3-gamemap-player-drawerOpen', 'true');
      localStorage.setItem('d3-gamemap-player-drawerActiveView', 'info');
    } else if (this._drawerActiveView !== 'info') {
      // Drawer is open to a different view: show badge
      this._infoBadge = true;
    }
    // If drawer is already on 'info': message is added, auto-scroll handled by the component
  }

  _saveInfoDrawerMessages() {
    if (!this.gameId) return;
    if (this.spectator) return;
    try {
      localStorage.setItem(
        `d3-gamemap-info-${this.gameId}`,
        JSON.stringify({
          messages: this._infoDrawerMessages.map((m) => ({ ...m, highlighted: false })),
          counter: this._infoMessageCounter,
        }),
      );
    } catch {
      // localStorage full — ignore
    }
  }

  _handleReadyClick() {
    this.submitAction({ type: 'waiting-room:ready' });
  }

  /**
   * Route a rest-action click. Server-emitted `rest:*` types open the
   * picker modal; the frontend-only `open:equipment-manager` synthetic
   * routes straight to the equipment screen.
   * @param {CustomEvent<import('../../types/jsdoc-types.js').RestAction>} e
   */
  _handleRestActionClick(e) {
    const action = e.detail;
    if (!action) return;
    if (action.type === 'open:equipment-manager') {
      this._equipmentScreenOpen = true;
      return;
    }
    this._openRestAction = action;
  }

  _handleRestActionCancel() {
    this._openRestAction = null;
  }

  /**
   * Submit the modal's selections and close it.
   * @param {CustomEvent<{ type: string, characterId: string, selections: string[] }>} e
   */
  _handleRestActionSubmit(e) {
    const { type, characterId, selections } = e.detail || {};
    if (!type || !characterId) return;
    this.submitAction({ type, characterId, selections: selections || [] });
    this._openRestAction = null;
  }

  _handleEndTurn() {
    this.submitAction({ type: 'turn:end' });
  }

  _handleExitGame() {
    this.dispatchEvent(new CustomEvent('exit-game', { bubbles: true }));
  }

  _handleEndGame() {
    this.dispatchEvent(new CustomEvent('end-game', { bubbles: true }));
  }

  /**
   * Spectator: clear the spectator session's local state and reload so
   * the SSE replays from event 0.
   */
  _handleSpectatorRewatch() {
    try {
      const prefix = `game-spectate-${this.gameId}`;
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k?.startsWith(prefix)) keysToRemove.push(k);
      }
      keysToRemove.forEach((k) => {
        localStorage.removeItem(k);
      });
    } catch {
      // localStorage unavailable — reload anyway
    }
    window.location.reload();
  }

  /**
   * Spectator: leave the spectator view, returning to the staff games list.
   */
  _handleSpectatorExit() {
    this.dispatchEvent(new CustomEvent('exit-spectator', { bubbles: true, composed: true }));
  }

  _handleSpectatorTogglePlay() {
    const ep = this._gameEvents.eventProcessor;
    if (!ep) return;
    if (ep.paused) {
      ep.resume();
      this._spectatorPaused = false;
    } else {
      ep.pause();
      this._spectatorPaused = true;
    }
  }

  _handleSpectatorStep() {
    const ep = this._gameEvents.eventProcessor;
    if (!ep) return;
    ep.step();
    this._spectatorPaused = true;
  }

  async _handleRetryGame() {
    // Game-over retry preserves the seed by design — the player
    // chose to retry the same scenario, not roll new dice (#113).
    await this._restartCurrentGame({ replaySeed: true });
  }

  /**
   * In-game menu's "Restart this adventure" entry (#338). Opens the
   * confirm dialog; the actual POST happens in _confirmRestartAdventure.
   */
  _handleRestartAdventure() {
    this._restartReplaySeed = false;
    this._showRestartConfirm = true;
  }

  async _confirmRestartAdventure() {
    const replaySeed = this._restartReplaySeed;
    this._showRestartConfirm = false;
    this._restartReplaySeed = false;
    await this._restartCurrentGame({ replaySeed });
  }

  _cancelRestartAdventure() {
    this._showRestartConfirm = false;
    this._restartReplaySeed = false;
  }

  /** @param {Event} e */
  _handleReplaySeedToggle(e) {
    this._restartReplaySeed = /** @type {HTMLInputElement} */ (e.target).checked;
  }

  /**
   * Shared helper for the game-over retry path and the in-game menu
   * "Restart this adventure" path (#338). POSTs to the restart
   * endpoint, clears localStorage so the reload doesn't flash the
   * pre-restart state, and forces a full page reload.
   * @param {{ replaySeed: boolean }} options
   */
  async _restartCurrentGame(options) {
    try {
      await api.post(`/api/games/${this.gameId}/restart`, {
        replaySeed: !!options?.replaySeed,
      });
      // No need for the owner's actual display name — the post-reload
      // banner reads "The game was restarted by you." from the owner's
      // perspective, and the SSE-replay guard in onGameRestarted will
      // prevent a duplicate reload when the broadcast event arrives
      // after the page comes back up.
      this._reloadForRestart('you');
    } catch (err) {
      console.error('Restart failed:', err);
    }
  }

  /**
   * Shared "the game just restarted, force a reload" path. Used by:
   *   - the owner's _restartCurrentGame after the POST returns OK
   *   - the SSE game:restarted handler for every other client
   *
   * Steps:
   *   1. Sweep localStorage + sessionStorage entries containing the
   *      gameId so the reload doesn't re-show stale state
   *      (partyWiped=true, dismissed help banners, etc.).
   *   2. Write a sessionStorage notice the post-reload page can read
   *      to render the "The game was restarted by {name}" banner.
   *   3. window.location.reload().
   *
   * Idempotent — race-safe if both the POST response and the SSE
   * event fire on the same tab.
   * @param {string | undefined} restarterName
   */
  _reloadForRestart(restarterName) {
    try {
      const lsKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.includes(this.gameId)) {
          lsKeys.push(key);
        }
      }
      for (const key of lsKeys) {
        localStorage.removeItem(key);
      }
      const ssKeys = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key?.includes(this.gameId)) {
          ssKeys.push(key);
        }
      }
      for (const key of ssKeys) {
        sessionStorage.removeItem(key);
      }
      if (restarterName) {
        sessionStorage.setItem(
          `game-restart-notice:${this.gameId}`,
          JSON.stringify({ restarterName, restartedAt: Date.now() }),
        );
      }
    } catch {
      /* storage unavailable — best effort */
    }
    window.location.reload();
  }

  /**
   * @param {CustomEvent} e
   */
  _handlePCClick(e) {
    const pc = e.detail;
    if (pc.playerId === this.currentUserId) {
      this._drawerActiveView = 'characterSheet';
      this._drawerOpen = true;
      this.requestUpdate();
    }
  }

  // Icon renderers
  _renderMenuIcon() {
    return html`<svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <line x1="4" x2="20" y1="12" y2="12" />
      <line x1="4" x2="20" y1="6" y2="6" />
      <line x1="4" x2="20" y1="18" y2="18" />
    </svg>`;
  }

  _renderSelectIcon() {
    return html`<svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <circle cx="12" cy="8" r="5" />
      <path d="M20 21a8 8 0 0 0-16 0" />
    </svg>`;
  }

  _renderPanIcon() {
    return html`<svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path d="M18 11V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2" />
      <path d="M14 10V4a2 2 0 0 0-2-2a2 2 0 0 0-2 2v2" />
      <path d="M10 10.5V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2v8" />
      <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
    </svg>`;
  }

  _renderEquipmentIcon() {
    return renderBackpackIcon();
  }

  _handleToggleEquipmentScreen() {
    this._equipmentScreenOpen = !this._equipmentScreenOpen;
  }

  _handleCloseEquipmentScreen() {
    this._equipmentScreenOpen = false;
  }

  /**
   * @param {CustomEvent} e
   */
  async _handleEquipmentScreenEquipItem(e) {
    await this.submitAction({ type: 'pc:equip-item', ...e.detail });
  }

  /**
   * @param {CustomEvent} e
   */
  _handleRequestPendingSwap(e) {
    // Stage the swap optimistically. The next pc:act:attack the player
    // submits will carry these as `prefixEquipmentChanges`. The server
    // commits swap+attack atomically — or rejects both. Phase defaults
    // to 'pre' here; only the picker's "Attack, then …" branch upgrades
    // to 'post' via _handleRequestPendingSwapAndAttack.
    this._pendingEquipmentSwap = {
      characterId: e.detail.characterId,
      changes: e.detail.changes,
      phase: 'pre',
    };
  }

  /**
   * Combined event from <equipment-screen>: stage the swap AND immediately
   * start attack-targeting for the chosen attack action. Closes the
   * equipment screen so the player can pick a target on the map.
   * @param {CustomEvent} e
   */
  _handleRequestPendingSwapAndAttack(e) {
    this._pendingEquipmentSwap = {
      characterId: e.detail.characterId,
      changes: e.detail.changes,
      // 'pre' (default) = swap applied BEFORE the attack; 'post' = AFTER.
      // _attachPendingPrefixSwap reads this to choose prefix vs postfix.
      phase: e.detail.phase || 'pre',
      // For 'pre' phase, the equipment-screen passes the post-swap
      // character record from preview-actions. _getMyCharacter overlays it
      // so targeting (range, target squares) reads the post-swap state
      // even though the snapshot is still pre-swap. #394.
      previewCharacter: e.detail.previewCharacter || null,
    };
    this._equipmentScreenOpen = false;
    // _handleStartTargeting expects `e.detail` to be the action object.
    this._handleStartTargeting(/** @type {any} */ ({ detail: e.detail.action }));
  }

  _handleCancelPendingSwap() {
    this._pendingEquipmentSwap = null;
  }

  _renderMobileToolbar() {
    return html`
      <div class="mobile-toolbar">
        <button class="mobile-tool-btn" @click="${() => (this._gameMenuOpen = true)}">
          <i class="bi bi-list"></i>
        </button>
        <button class="mobile-tool-btn" @click="${() => {
          this._mobileSheetView = this._mobileSheetView === 'info' ? null : 'info';
          this._infoBadge = false;
        }}">
          <i class="bi bi-info-circle"></i>
          ${this._infoBadge ? html`<span class="badge-dot"></span>` : ''}
        </button>
        <button class="mobile-tool-btn" @click="${() => {
          this._mobileSheetView = this._mobileSheetView === 'characterSheet' ? null : 'characterSheet';
        }}">
          <i class="bi bi-person"></i>
        </button>
        ${
          !this.spectator
            ? html`
        <button class="mobile-tool-btn" @click="${() => {
          this._mobileSheetView = this._mobileSheetView === 'effects' ? null : 'effects';
        }}">
          <i class="bi bi-stars"></i>
        </button>
        `
            : ''
        }
        <button class="mobile-tool-btn" @click="${this._handleCenterOnTokens}">
          <i class="bi bi-crosshair"></i>
        </button>
      </div>
    `;
  }

  _renderMobileBottomSheet() {
    if (!this._mobileSheetView) return '';

    const titles = { info: 'Messages', characterSheet: 'Character', effects: 'Effects' };
    const title = titles[this._mobileSheetView] || '';

    return html`
      <div class="mobile-sheet-backdrop" @click="${() => {
        this._mobileSheetView = null;
      }}"></div>
      <div class="mobile-bottom-sheet ${this._mobileSheetView === 'characterSheet' ? 'dark' : ''}">
        <div class="mobile-sheet-handle"><div class="mobile-sheet-handle-pill"></div></div>
        <div class="mobile-sheet-header">
          <span class="mobile-sheet-title">${title}</span>
          <button class="mobile-sheet-close" @click="${() => {
            this._mobileSheetView = null;
          }}">Done</button>
        </div>
        <div class="mobile-sheet-content">
          ${
            this._mobileSheetView === 'characterSheet'
              ? html`<character-sheet-drawer
                .characters="${mapState.playerCharacters.filter((c) => c.playerId === this.currentUserId)}"
                current-user-id="${this.currentUserId}"
                @equip-item="${(/** @type {CustomEvent} */ e) => this.submitAction({ type: 'pc:equip-item', ...e.detail })}"
                @open-equipment-screen="${() => {
                  this._mobileSheetView = null;
                  this._equipmentScreenOpen = true;
                }}"
              ></character-sheet-drawer>`
              : ''
          }
          ${
            this._mobileSheetView === 'info'
              ? html`<info-drawer .messages="${this._infoDrawerMessages}"></info-drawer>`
              : ''
          }
          ${
            this._mobileSheetView === 'effects'
              ? html`<effects-drawer .characters="${mapState.playerCharacters}"></effects-drawer>`
              : ''
          }
        </div>
      </div>
    `;
  }

  render() {
    const canvasClass = `game-map ${this._mode === 'pan' ? 'mode-pan' : ''} ${this._isPanning ? 'is-panning' : ''} ${this._movementCursorClass}`;

    return html`
      <div class="game-map-wrapper ${platformState.isMobile ? 'mobile' : ''}">
        <div class="game-map-container">
          <canvas class="${canvasClass}" width="${this._canvasWidth}" height="${this._canvasHeight}"></canvas>

          ${
            this._imagesLoading
              ? html`
                <div class="loading-overlay">
                  <div class="loading-spinner"></div>
                  <div class="loading-text">Loading map images...</div>
                </div>
              `
              : ''
          }

          ${
            platformState.isMobile
              ? this._renderMobileToolbar()
              : html`
          <!-- Toolbar -->
          <div class="tools-sidebar">
            <toolbar-well>
              <toolbar-button tooltip="Game Menu" @click="${() => (this._gameMenuOpen = true)}">
                ${this._renderMenuIcon()}
              </toolbar-button>
              <div class="tool-separator"></div>
              <toolbar-button
                ?active="${this._mode === 'select'}"
                tooltip="Select Tool"
                @click="${this._handleToggleSelectMode}"
              >
                ${this._renderSelectIcon()}
              </toolbar-button>
              <toolbar-button
                ?active="${this._mode === 'pan' || this._isPanning}"
                tooltip="Pan Tool"
                @click="${this._handleTogglePanMode}"
              >
                ${this._renderPanIcon()}
              </toolbar-button>
              <div class="tool-separator"></div>
              <toolbar-button
                ?active="${this._equipmentScreenOpen}"
                tooltip="Equipment"
                @click="${this._handleToggleEquipmentScreen}"
              >
                ${this._renderEquipmentIcon()}
              </toolbar-button>
            </toolbar-well>
          </div>

          <!-- Grid Debug Display -->
          ${
            this._debug
              ? html`
            <div class="grid-debug">
              ${
                mapState.seed != null
                  ? html`
                <div class="seed-row">
                  <span>Seed: ${mapState.seed}</span>
                  <button
                    class="seed-copy"
                    title="${this._seedCopied ? 'Copied!' : 'Copy seed'}"
                    @click="${this._handleCopySeed}"
                  >
                    <i class="bi ${this._seedCopied ? 'bi-check2' : 'bi-clipboard'}"></i>
                  </button>
                </div>
              `
                  : ''
              }
              <div>Grid: (${this._currentGridCol}, ${this._currentGridRow})</div>
            </div>
          `
              : ''
          }
          `
          }

          ${
            !platformState.isMobile
              ? html`
          <!-- View Controls -->
          <view-controls
            zoom="${viewState.zoom}"
            ?show-walls="${this._showWalls}"
            .showGridToggle="${false}"
            ?show-playback-controls="${this.spectator}"
            ?paused="${this._spectatorPaused}"
            ?spectator-badge="${this.spectator}"
            @zoom-in="${this._handleZoomIn}"
            @zoom-out="${this._handleZoomOut}"
            @reset-zoom="${this._handleResetZoom}"
            @toggle-walls="${this._handleToggleWalls}"
            @center-on-tokens="${this._handleCenterOnTokens}"
            @playback-restart="${this._handleSpectatorRewatch}"
            @playback-step="${this._handleSpectatorStep}"
            @playback-toggle="${this._handleSpectatorTogglePlay}"
          ></view-controls>
          `
              : ''
          }

          ${
            !platformState.isMobile
              ? html`
          <!-- Music Controls -->
          <music-controls></music-controls>

          <!-- Drawer Bar (hidden during the prep-phase lobby; the drawer is
               forced open showing the gathering, no toggling allowed.) -->
          ${
            mapState.waitingRoom?.isActive === true
              ? ''
              : html`
          <drawer-bar
            ?drawer-open="${this._drawerOpen}"
            active-view="${this._drawerActiveView || ''}"
            ?show-users="${!this.spectator}"
            ?show-character-sheet="${!this.spectator}"
            ?show-effects="${!this.spectator}"
            show-info
            ?info-badge="${this._infoBadge}"
            @close-drawer="${this._handleCloseDrawer}"
            @toggle-view="${(/** @type {CustomEvent} */ e) => this._handleToggleDrawerView(e.detail)}"
          ></drawer-bar>
          `
          }
          `
              : ''
          }

          <!-- Top Banners Container -->
          <div class="top-banners-container">
            <game-restart-banner game-id="${this.gameId}"></game-restart-banner>
            ${
              !platformState.isMobile
                ? html`
            <turn-banner
              ?show="${
                this._turnState !== null &&
                !this._isPlacementMode &&
                !this._isWaitingForOtherPlayers &&
                !mapState.waitingRoom?.isActive
              }"
              .turnState="${this._turnState}"
              current-user-id="${this.currentUserId}"
              ?spectator="${this.spectator}"
              .players="${mapState.gamePlayers}"
              .monsterPlacements="${mapState.monsterPlacements}"
              .playerCharacters="${mapState.playerCharacters}"
              .actions="${this._getMyCharacter()?.actions || []}"
              @end-turn="${this._handleEndTurn}"
              @start-targeting="${this._handleStartTargeting}"
              @execute-action="${this._handleExecuteAction}"
              @show-actions-modal="${() => (this._showActionsModal = true)}"
            ></turn-banner>
            `
                : ''
            }

            <prep-phase-banner
              ?show="${mapState.waitingRoom?.isActive === true}"
              .waitingRoom="${mapState.waitingRoom}"
              .playerCharacters="${mapState.playerCharacters}"
              current-user-id="${this.currentUserId}"
              @ready-click="${this._handleReadyClick}"
              @rest-action-click="${this._handleRestActionClick}"
            ></prep-phase-banner>

            <placement-banner
              ?show="${this._isPlacementMode}"
              objective="${mapState.objective || ''}"
            ></placement-banner>

            <waiting-banner
              ?show="${this._isWaitingForOtherPlayers && !mapState.waitingRoom?.isActive}"
            ></waiting-banner>
          </div>

          <!-- Pending extra-attack offer prompt (Cleave?) — true modal:
               an absolute-positioned backdrop blocks all other clicks
               in the map container until the player picks Skip or accepts.
               Hidden while we're already in targeting mode for this same
               offer (player has chosen to attack and is now picking a
               target). -->
          ${(() => {
            const me = this._getMyCharacter();
            const offer = me?.pendingExtraAttackOffer;
            if (!offer) return '';
            if (
              interactionState.mode === 'attack-targeting' &&
              interactionState.attackTargeting?.actionId === `extra-attack:${offer.slug}`
            ) {
              return '';
            }
            // Hold the modal until in-flight event animations (callouts,
            // future movement reveals, etc.) settle — otherwise the prompt
            // covers the "-N damage" labels mid-animation. The tracker
            // notifies on idle/active transitions and we re-render on it.
            if (!eventAnimationState.isIdle()) return '';
            return html`
              <div class="extra-attack-modal-backdrop" @click="${(/** @type {MouseEvent} */ e) => e.stopPropagation()}">
                <div class="extra-attack-prompt" @click="${(/** @type {MouseEvent} */ e) => e.stopPropagation()}">
                  <div class="extra-attack-prompt-text">
                    ${offer.name || 'Extra attack'}?
                    ${offer.description ? html`<div class="extra-attack-prompt-description">${offer.description}</div>` : ''}
                  </div>
                  <div class="extra-attack-prompt-actions">
                    <button
                      class="extra-attack-skip-btn"
                      @click="${() => this._handleDeclineExtraAttackOffer(offer)}"
                    >
                      Skip
                    </button>
                    <button
                      class="extra-attack-accept-btn"
                      ?disabled="${!offer.validTargetIds || offer.validTargetIds.length === 0}"
                      @click="${() => this._handleAcceptExtraAttackOffer(offer)}"
                    >
                      ${offer.name || 'Attack'}
                    </button>
                  </div>
                </div>
              </div>
            `;
          })()}

          <!-- Targeting Banner (fixed bottom-center) -->
          ${
            interactionState.mode === 'attack-targeting' && this._pendingAction
              ? html`
                <div class="targeting-banner">
                  <span class="targeting-icon">
                    ${
                      this._pendingAction.slug === 'ranged-weapon-attack'
                        ? html`
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <circle cx="12" cy="12" r="6" />
                            <circle cx="12" cy="12" r="2" />
                            <line x1="22" x2="18" y1="12" y2="12" />
                            <line x1="6" x2="2" y1="12" y2="12" />
                            <line x1="12" x2="12" y1="6" y2="2" />
                            <line x1="12" x2="12" y1="22" y2="18" />
                          </svg>
                        `
                        : this._pendingAction.category === 'spell'
                          ? html`
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <path
                                d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"
                              />
                              <path d="M20 3v4" />
                              <path d="M22 5h-4" />
                              <path d="M4 17v2" />
                              <path d="M5 18H3" />
                            </svg>
                          `
                          : html`
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <path d="M14.5 17.5 3 6V3h3l11.5 11.5" />
                              <path d="M13 19l6-6" />
                              <path d="m16 16 3.5 3.5" />
                              <path d="m19 19 2 2" />
                              <path d="M14.5 6.5 18 3h3v3l-3.5 3.5" />
                              <path d="m5 14 4 4" />
                              <path d="m7 17-2 2" />
                              <path d="m3 19 2 2" />
                            </svg>
                          `
                    }
                  </span>
                  <span class="targeting-text">Choose ${this._pendingAction.name} Target${interactionState.attackTargeting.maxTargets > 1 ? ` (${interactionState.attackTargeting.selectedTargets.length}/${interactionState.attackTargeting.maxTargets})` : ''}</span>
                  ${
                    interactionState.attackTargeting.selectedTargets.length > 0 &&
                    interactionState.attackTargeting.maxTargets > 1
                      ? html`
                        <button
                          class="confirm-targeting-btn"
                          @click="${() => this._executeMultiTargetAction(interactionState.attackTargeting.selectedTargets)}"
                        >
                          ${this._pendingAction.category === 'spell' ? 'Cast' : 'Confirm'}
                        </button>
                      `
                      : ''
                  }
                  <button
                    class="cancel-targeting-btn"
                    @click="${() => {
                      interactionState.clearAttackTargeting();
                      this._pendingAction = null;
                      this._pendingActionChoices = null;
                    }}"
                  >
                    Cancel
                  </button>
                </div>
              `
              : ''
          }

          <!-- Door Panel -->
          <door-panel .door="${this._activeDoor}" @close="${() => (this._activeDoor = null)}"></door-panel>

          <!-- Callouts (server-driven floating text) -->
          ${this._callouts.map(
            (c) => html`
              <callout-label
                text="${c.text}"
                x="${c.x}"
                y="${c.y}"
                color="${c.color}"
                delay-ms="${c.delayMs}"
                hold-ms="${c.holdMs}"
                fade-ms="${c.fadeMs}"
                @complete="${() => this._removeCallout(c.id)}"
              ></callout-label>
            `,
          )}

          <!-- Initiative Reveal Panel -->
          ${
            this._initiativeRevealState
              ? html`
                <initiative-reveal
                  .combatState="${this._initiativeRevealState}"
                  current-user-id="${this.currentUserId}"
                  @reveal-complete="${this._onInitiativeRevealComplete}"
                ></initiative-reveal>
              `
              : ''
          }

          <!-- Center Announcement -->
          ${
            this._centerAnnouncement
              ? html`
                <center-announcement
                  message="${this._centerAnnouncement.message}"
                  type="${this._centerAnnouncement.type}"
                  @complete="${() => (this._centerAnnouncement = null)}"
                ></center-announcement>
              `
              : ''
          }

          ${
            !platformState.isMobile
              ? html`
          <!-- Rotate Cursor -->
          <rotate-cursor
            ?show="${this._showRotateCursor}"
            .x="${this._rotateCursorX}"
            .y="${this._rotateCursorY}"
          ></rotate-cursor>
          `
              : ''
          }

          <!-- Victory / Game-Over: hidden in spectator mode; spectator gets its own overlay below -->
          ${
            this.spectator
              ? ''
              : html`
            <victory-overlay ?show="${this._adventureCompleted}" @end-game="${this._handleEndGame}"></victory-overlay>
            <game-over-overlay
              ?show="${this._partyWiped}"
              @quit-game="${this._handleEndGame}"
              @retry-game="${this._handleRetryGame}"
            ></game-over-overlay>
          `
          }
          ${
            this.spectator
              ? html`
            <spectator-end-overlay
              ?show="${this._adventureCompleted || this._partyWiped}"
              outcome="${this._partyWiped ? 'wiped' : 'completed'}"
              @rewatch="${this._handleSpectatorRewatch}"
              @exit-spectator="${this._handleSpectatorExit}"
            ></spectator-end-overlay>
          `
              : ''
          }

          <!-- First-time help messages: registry-driven; add entries to HELP_MESSAGES above. -->
          ${HELP_MESSAGES.map(
            (m) => html`
              <help-message
                help-id="${m.id}"
                heading="${m.heading}"
                ?show="${this._activeHelpId === m.id}"
                @dismiss="${this._handleHelpDismiss}"
              >
                ${m.body(html)}
              </help-message>
            `,
          )}

          <!-- Action / Bonus-Action modals. Rendered as a direct child of
               .game-map-container (sibling of .top-banners-container) so the
               position:absolute backdrop with inset:0 sizes to the visible
               game viewport. game-map-wrapper is a flex row that includes
               the drawer-panel, so we MUST stay inside game-map-container —
               otherwise the modal covers the drawer too. -->
          ${this._showActionsModal ? this._renderActionsModal() : ''}
          ${this._readyChooserAction ? this._renderReadyChooserModal() : ''}
          ${this._spellChoicesContext ? this._renderSpellChoicesModal() : ''}

          ${
            this._openRestAction
              ? html`<rest-action-modal
                .action="${this._openRestAction}"
                @rest-action-submit="${this._handleRestActionSubmit}"
                @rest-action-cancel="${this._handleRestActionCancel}"
              ></rest-action-modal>`
              : ''
          }
          ${this._emptySquareConfirm ? this._renderEmptySquareConfirm() : ''}
        </div>

        ${
          platformState.isMobile
            ? html`
        <mobile-turn-bar
          ?show="${
            this._turnState !== null &&
            !this._isPlacementMode &&
            !this._isWaitingForOtherPlayers &&
            !mapState.waitingRoom?.isActive
          }"
          .turnState="${this._turnState}"
          current-user-id="${this.currentUserId}"
          .playerCharacters="${mapState.playerCharacters}"
          .actions="${this._getMyCharacter()?.actions || []}"
          @end-turn="${this._handleEndTurn}"
          @start-targeting="${this._handleStartTargeting}"
          @execute-action="${this._handleExecuteAction}"
        ></mobile-turn-bar>
        `
            : ''
        }

        ${
          platformState.isMobile
            ? this._renderMobileBottomSheet()
            : html`
        <!-- Drawer -->
        <drawer-panel ?open="${mapState.waitingRoom?.isActive === true || this._drawerOpen}">
          ${
            mapState.waitingRoom?.isActive === true
              ? html`
                <div class="drawer-view">
                  <tavern-waiting-banner
                    show
                    .waitingRoom="${mapState.waitingRoom}"
                    .players="${mapState.gamePlayers}"
                    .invitedUsers="${mapState.invitedUsers}"
                    .playerCharacters="${mapState.playerCharacters}"
                    current-user-id="${this.currentUserId}"
                  ></tavern-waiting-banner>
                </div>
              `
              : ''
          }
          ${
            mapState.waitingRoom?.isActive !== true && this._drawerActiveView === 'characterSheet'
              ? html`
                <div class="drawer-view">
                  <character-sheet-drawer
                    .characters="${mapState.playerCharacters.filter((c) => c.playerId === this.currentUserId)}"
                    current-user-id="${this.currentUserId}"
                    @equip-item="${(/** @type {CustomEvent} */ e) =>
                      this.submitAction({ type: 'pc:equip-item', ...e.detail })}"
                    @open-equipment-screen="${() => (this._equipmentScreenOpen = true)}"
                  ></character-sheet-drawer>
                </div>
              `
              : ''
          }
          ${
            mapState.waitingRoom?.isActive !== true && this._drawerActiveView === 'usersList'
              ? html`
                <div class="drawer-view">
                  <users-list
                    .players="${mapState.gamePlayers}"
                    .playerCharacters="${mapState.playerCharacters}"
                    .monsterPlacements="${mapState.monsterPlacements}"
                    current-user-id="${this.currentUserId}"
                    @pc-click="${this._handlePCClick}"
                  ></users-list>
                </div>
              `
              : ''
          }
          ${
            mapState.waitingRoom?.isActive !== true && this._drawerActiveView === 'info'
              ? html`
                <div class="drawer-view" style="overflow-y: hidden;">
                  <info-drawer .messages="${this._infoDrawerMessages}"></info-drawer>
                </div>
              `
              : ''
          }
          ${
            mapState.waitingRoom?.isActive !== true && this._drawerActiveView === 'effects'
              ? html`
                <div class="drawer-view">
                  <effects-drawer .characters="${mapState.playerCharacters}"></effects-drawer>
                </div>
              `
              : ''
          }
        </drawer-panel>
        `
        }

        <!-- Equipment Screen -->
        ${
          this._equipmentScreenOpen
            ? html`<equipment-screen
              game-id="${this.gameId}"
              .character="${this._getMyCharacter()}"
              .pendingSwap="${this._pendingEquipmentSwap}"
              .combatTime="${!!this._turnState?.combatTime}"
              .isMyTurn="${this._turnState?.currentPlayerID === this.currentUserId}"
              @close-equipment-screen="${this._handleCloseEquipmentScreen}"
              @equip-item="${this._handleEquipmentScreenEquipItem}"
              @request-pending-swap="${this._handleRequestPendingSwap}"
              @request-pending-swap-and-attack="${this._handleRequestPendingSwapAndAttack}"
              @cancel-pending-swap="${this._handleCancelPendingSwap}"
            ></equipment-screen>`
            : ''
        }

        <!-- Game Menu -->
        <game-menu
          ?open="${this._gameMenuOpen}"
          @close="${() => (this._gameMenuOpen = false)}"
          @exit-game="${this._handleExitGame}"
          @restart-adventure="${this._handleRestartAdventure}"
        ></game-menu>
        ${
          this._showRestartConfirm
            ? html`
              <confirm-delete
                ?show="${true}"
                heading="Restart this adventure"
                message="This will delete your current game and start a new one. You will lose all progress and your character will be reset to before the adventure started. Are you sure?"
                confirm-label="Restart"
                @confirm="${this._confirmRestartAdventure}"
                @cancel="${this._cancelRestartAdventure}"
              >
                ${
                  this._debug
                    ? html`
                      <label
                        style="display:flex;align-items:center;gap:0.5rem;margin-top:0.75rem;padding:0.5rem 0.75rem;background:#fff7e6;border:1px dashed #b08040;border-radius:6px;color:#5a3a10;cursor:pointer;"
                      >
                        <input
                          type="checkbox"
                          .checked="${this._restartReplaySeed}"
                          @change="${this._handleReplaySeedToggle}"
                        />
                        <span>
                          <strong>Debug</strong> · Replay current seed
                        </span>
                      </label>
                    `
                    : ''
                }
              </confirm-delete>
            `
            : ''
        }
      </div>
    `;
  }

  /**
   * @param {number} id
   */
  _removeCallout(id) {
    this._callouts = this._callouts.filter((c) => c.id !== id);
    eventAnimationState.end(`callout:${id}`);
  }

  // === Map placeables helpers (PCs + monster placements) ===

  /**
   * Build the union of player characters and monster placements as MapPlaceable[].
   * Each entry has a `_kind` discriminator: 'pc' or 'monster'.
   * PCs without a position are filtered out.
   * @returns {Array<{_kind: 'pc' | 'monster', id: string, col: number, row: number, direction?: string, size?: string, imageUrl?: string, name?: string, defeated?: boolean, playerId?: string, [key: string]: *}>}
   */
  _getMapPlaceables() {
    /** @type {Array<*>} */
    const placeables = [];
    for (const pc of mapState.playerCharacters) {
      if (pc.col === undefined || pc.row === undefined) continue;
      placeables.push({
        _kind: 'pc',
        id: pc.id,
        col: pc.col,
        row: pc.row,
        direction: pc.direction || 'down',
        size: 'medium',
        imageUrl: pc.token?.imageUrl,
        name: pc.name,
        playerId: pc.playerId,
        currentHP: pc.hp ?? pc.currentHP,
        maxHP: pc.maxHp ?? pc.maxHP,
        ac: pc.ac,
        hasCircleBorder: pc.token?.hasCircleBorder || false,
        borderColor: pc.token?.borderColor || '#000000',
        conditions: pc.conditions,
      });
    }
    for (const mp of mapState.monsterPlacements) {
      placeables.push({
        _kind: 'monster',
        ...mp,
      });
    }
    return placeables;
  }

  // Preload the images the first frame needs, then drop the loading
  // overlay. Safe to call more than once (preloadImages caches and the
  // flag is already a boolean), so both the catch-up and SSE-connect
  // paths can call it without racing.
  _revealMap() {
    this._preloadMapImages().then(() => {
      this._imagesLoading = false;
    });
  }

  /**
   * Build the set of image URLs needed to render the first frame and
   * await all of them. Resolves once every image has loaded (or errored
   * / timed out) so the loading overlay can hide on a fully-painted map.
   * @returns {Promise<void>}
   */
  async _preloadMapImages() {
    /** @type {Set<string>} */
    const urls = new Set();

    if (mapState.backgroundUrl) {
      urls.add(this._absolutizeUrl(mapState.backgroundUrl));
    }
    for (const p of this._getMapPlaceables()) {
      if (p.imageUrl) urls.add(this._absolutizeUrl(p.imageUrl));
    }

    await preloadImages(urls);
  }

  /**
   * Resolve a server-relative `/...` URL against the configured backend
   * origin so the preloader hits the same URL the renderer will. Pass
   * full URLs through unchanged.
   * @param {string} url
   * @returns {string}
   */
  _absolutizeUrl(url) {
    if (url?.startsWith('/') && this.backendUrl) {
      return this.backendUrl + url;
    }
    return url;
  }

  // Public API for external use

  /**
   * Submit a game action to the backend and apply the returned events
   * @param {object} action - Action object with type and data
   * @returns {Promise<object[]>} Array of events returned by the backend
   */
  async submitAction(action) {
    if (this.spectator) {
      // Spectator mode is read-only; ignore any action submission that
      // slipped through despite the suppressed input affordances.
      return;
    }

    // api.post fetches a fresh access token per call via
    // authState.getAccessToken, so long sessions don't 403 once the
    // initial token rotates past nearExpiry.
    const data = await api.post(`/api/games/${this.gameId}/actions`, action);

    // Backend returns single event or array of events
    const events = Array.isArray(data) ? data : [data];

    // Apply each returned event
    for (const event of events) {
      await this._gameEvents.eventProcessor?.applyEvent(event);
    }

    return events;
  }

  /**
   * Render server-generated callouts. Each callout's anchor is resolved to a
   * screen position, then the label is scheduled by its DelayMs.
   *
   * @param {Array<{anchor: {type: string, data: *}, text: string, color: string, delayMs: number, holdMs: number, fadeMs: number}>} callouts
   */
  showCallouts(callouts) {
    if (!Array.isArray(callouts) || callouts.length === 0) return;

    // Push every callout onto the render list immediately. Each
    // <callout-label> handles its own delay/hold/fade via a single
    // rAF loop, so all callouts in the volley share one timeline and
    // can't drift relative to each other. Successive callouts on the
    // same anchor are separated visually by the rise-during-fade
    // animation: by the time dart N+1 appears, dart N has already
    // started rising and fading.
    const additions = [];
    for (const c of callouts) {
      if (!c.anchor) continue;

      let col;
      let row;
      let actorId = '';

      if (c.anchor.type === 'actor') {
        actorId = c.anchor.data?.actorId;
        if (!actorId) continue;
        const placeable = this._getMapPlaceables().find((p) => p.id === actorId);
        if (!placeable) continue;
        col = placeable.col;
        row = placeable.row;
      } else if (c.anchor.type === 'cell') {
        // Cell anchor: server-supplied grid coordinate. Used for
        // unseen-attacker callouts (#162) where the attacker's
        // placement isn't even on the client. Render at the cell's
        // screen position, but clamp to the visible canvas edge if
        // the cell is currently off-screen so the player still sees
        // the callout drawing attention to the attack.
        const data = c.anchor.data || {};
        if (typeof data.col !== 'number' || typeof data.row !== 'number') continue;
        col = data.col;
        row = data.row;
      } else {
        continue;
      }

      let pos = this._gridToScreenPosition(col, row);
      if (c.anchor.type === 'cell') {
        pos = this._clampPositionToViewport(pos);
      }
      additions.push({
        id: ++this._calloutIdCounter,
        actorId,
        text: c.text,
        x: pos.x,
        y: pos.y,
        color: c.color,
        delayMs: c.delayMs || 0,
        holdMs: c.holdMs,
        fadeMs: c.fadeMs,
      });
    }
    if (additions.length > 0) {
      this._callouts = [...this._callouts, ...additions];
      for (const a of additions) {
        // Register each callout as an in-flight event animation so any
        // consumer that defers UI until the animation queue is idle
        // (e.g. the extra-attack prompt modal) waits for them.
        eventAnimationState.begin(`callout:${a.id}`);
        // Safety net: the existing TODO in _removeCallout flags that
        // <callout-label>'s `complete` event can be dropped (parent
        // re-render, map teardown). End the tracker entry on a timer
        // tied to the animation's natural duration so a lost `complete`
        // doesn't leave the tracker permanently active. The natural
        // path runs first via _removeCallout → no-op end here.
        const totalMs = (a.delayMs || 0) + (a.holdMs || 0) + (a.fadeMs || 0) + 200;
        setTimeout(() => eventAnimationState.end(`callout:${a.id}`), totalMs);
      }
    }
  }

  /**
   * @param {string} message
   * @param {string} type
   */
  showCenterAnnouncement(message, type) {
    this._centerAnnouncement = { message, type };
  }

  _onInitiativeRevealComplete() {
    this._initiativeRevealState = null;
    if (this._initiativeRevealResolve) {
      this._initiativeRevealResolve();
      this._initiativeRevealResolve = null;
    }
  }

  /**
   * @param {number} col
   * @param {number} row
   * @returns {{ x: number, y: number }}
   */
  _gridToScreenPosition(col, row) {
    const cellCenterX = (col * mapState.gridSize + mapState.gridSize / 2) * viewState.zoom + viewState.panX;
    const cellCenterY = row * mapState.gridSize * viewState.zoom + viewState.panY;
    return { x: cellCenterX, y: cellCenterY };
  }

  /**
   * Clamp a screen-space position into the visible canvas rect with a
   * small margin so a callout label stays fully readable when it
   * lands near the edge. Used for cell-anchored callouts (#162) so a
   * shot from off-screen still surfaces a callout the player can see.
   *
   * @param {{ x: number, y: number }} pos
   * @returns {{ x: number, y: number }}
   */
  _clampPositionToViewport(pos) {
    const margin = 60;
    const w = this._canvas?.clientWidth ?? this.clientWidth ?? 0;
    const h = this._canvas?.clientHeight ?? this.clientHeight ?? 0;
    if (w <= 0 || h <= 0) return pos;
    return {
      x: Math.max(margin, Math.min(pos.x, w - margin)),
      y: Math.max(margin, Math.min(pos.y, h - margin)),
    };
  }
}

customElements.define('game-map', GameMap);
